/* eslint-disable max-len */
import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import camelCase from 'lodash.camelcase';
import upperFirst from 'lodash.upperfirst';

import {
  ASSET_PLACEHOLDER,
  CATEGORIES,
  CHART_TYPE_COLUMN,
  CHART_TYPE_TIMESERIES,
  CURRENCY_PLACEHOLDER,
  CURRENCY_USD,
  FORMATTER_CURRENCY,
  FORMATTER_CURRENCY_USD,
  FORMATTER_NUMBER,
  FORMATTER_PERCENT,
  FORMATTER_PERCENT_PRECISE,
  FORMATTER_SECOND,
  FORMATTER_TH_PER_SECOND,
  FORMATTER_TRILLION,
  FORMATTER_UNIT_PER_SECOND,
  RESOLUTION_1H,
  SCALE_LIN,
  SCALE_LOG,
  TIER_1,
  TIER_2,
  TIER_3,
} from './app';
import * as params from './params';

import { WITH_INTERNAL_METRICS } from './features';

export const MAX_DAYS_TILL_HALVING = 1420;

export const WEI_TO_GWEI_MULTIPLIER = 0.000000001;
export const TERA_MULTIPLIER = 0.000000000001;
export const INDEX_MULTIPLIER = 100;

export const GCI_CODE = 'indexes.Gci';

export const CDD_ADJUSTED_BINARY_CODE = 'indicators.CddSupplyAdjustedBinary';
export const HODLER_NET_POSITION_CODE = 'indicators.HodlerNetPositionChange';
export const MVRV_ACCOUNT_BASED_CODE = 'indicators.MvrvAccountBased';
export const NET_REALIZED_PROFIT_LOSS_CODE = 'indicators.NetRealizedProfitLoss';
export const PUELL_MULTIPLE_CODE = 'indicators.PuellMultiple';
export const RESERVE_RISK_CODE = 'indicators.ReserveRisk';
export const SOAB_CODE = 'indicators.Soab';
export const SOPD_ATH_CODE = 'indicators.SpentOutputPriceDistributionAth';
export const SOPD_PERCENT_CODE = 'indicators.SpentOutputPriceDistributionPercent';
export const SOPR_ACCOUNT_BASED_CODE = 'indicators.SoprAccountBased';
export const SOPR_ADJUSTED_CODE = 'indicators.SoprAdjusted';
export const SOPR_CODE = 'indicators.Sopr';
export const SOPR_LESS_155_CODE = 'indicators.SoprLess155';
export const SOPR_MORE_155_CODE = 'indicators.SoprMore155';
export const SSR_CODE = 'indicators.Ssr';
export const STOCK_TO_FLOW_DEFLECTION_CODE = 'indicators.StockToFlowDeflection';
export const STOCK_TO_FLOW_RATIO_CODE = 'indicators.StockToFlowRatio';
export const UNREALIZED_PROFIT_LOSS_CODE = 'indicators.NetUnrealizedProfitLoss';
export const UNREALIZED_PROFIT_LOSS_LTH_CODE = 'indicators.NuplMore155';
export const UNREALIZED_PROFIT_LOSS_STH_CODE = 'indicators.NuplLess155';
export const DIFFICULTY_RIBBON_CODE = 'indicators.DifficultyRibbon';
export const DIFFICULTY_RIBBON_COMPRESSION_CODE = 'indicators.DifficultyRibbonCompression';
export const HASH_RIBBON_CODE = 'indicators.HashRibbon';

export const URPD_ATH_CODE = 'indicators.UtxoRealizedPriceDistributionAth';
export const URPD_PERCENT_CODE = 'indicators.UtxoRealizedPriceDistributionPercent';

export const NUPL_ACC_BASED_CODE = 'indicators.NetUnrealizedProfitLossAccountBased';
export const NUPL_LESS_155_ACC_BASED_CODE = 'indicators.NuplLess155AccountBased';
export const NUPL_MORE_155_ACC_BASED_CODE = 'indicators.NuplMore155AccountBased';

export const MVRV_CODE = 'market.Mvrv';
export const MVRV_LESS_155_CODE = 'market.MvrvLess155';
export const MVRV_MORE_155_CODE = 'market.MvrvMore155';
export const MVRV_Z_CODE = 'market.MvrvZScore';
export const MARKET_CAP_CODE = 'market.MarketcapUsd';
export const PRICE_USD_CODE = 'market.PriceUsdClose';
export const PRICE_USD_OHLC_CODE = 'market.PriceUsdOhlc';
export const PRICE_DRAWDOWN_RELATIVE_CODE = 'market.PriceDrawdownRelative';

export const CURRENT_SUPPLY_CODE = 'supply.Current';
export const HODL_WAVES_CODE = 'supply.HodlWaves';
export const HODL_WAVES_RCAP_CODE = 'supply.RcapHodlWaves';
export const INFLATION_RATE_CODE = 'supply.InflationRate';
export const ISSUANCE_CODE = 'supply.Issued';
export const SUPPLY_LTH_NET_CHANGE_CODE = 'supply.LthNetChange';
export const SUPPLY_LTH_STH_PROFIT_LOSS_RELATIVE_CODE = 'supply.LthSthProfitLossRelative';
export const SUPPLY_PROFIT_RELATIVE_CODE = 'supply.ProfitRelative';

export const DERIVATIVES_FUNDING_RATE_PERPETUAL_CODE = 'derivatives.FuturesFundingRatePerpetual';
export const DERIVATIVES_OPEN_INTEREST_CODE = 'derivatives.FuturesOpenInterestAllSum';
export const DERIVATIVES_OPEN_INTEREST_PERPETUAL_CODE = 'derivatives.FuturesOpenInterestPerpetualSum';
export const DERIVATIVES_VOLUME_DAILY_CODE = 'derivatives.FuturesVolumeDailyAllSum';
export const DERIVATIVES_VOLUME_DAILY_PERPETUAL_CODE = 'derivatives.FuturesVolumeDailyPerpetualSum';

export const DERIVATIVES_VOLUME_LATEST = 'derivatives.FuturesVolumeDailyLatest';
export const DERIVATIVES_OPEN_INTEREST_LATEST = 'derivatives.FuturesOpenInterestLatest';

export const ENTITIES_PROFIT_RELATIVE_CODE = 'entities.ProfitRelative';
export const ENTITIES_SUPPLY_DISTRIBUTION_RELATIVE_CODE = 'entities.SupplyDistributionRelative';

export const ADDRESSES_PROFIT_RELATIVE_CODE = 'addresses.ProfitRelative';
export const EXCHANGE_BALANCE_CODE = 'distribution.BalanceExchanges';
export const EXCHANGE_BALANCE_ALL_CODE = 'distribution.BalanceExchangesAll';
export const MINERS_BALANCE_ALL_CODE = 'distribution.BalanceMinersAll';
export const EXCHANGE_INFLOW_CODE = 'transactions.TransfersVolumeToExchangesSum';
export const EXCHANGE_NET_FLOW = 'transactions.TransfersVolumeExchangesNet';
export const TRANSFERS_VOLUME_MINERS_ALL = 'transactions.TransfersVolumeMinersToExchangesAll';
export const TRANSFERS_VOLUME_MINERS_NET = 'transactions.TransfersVolumeMinersNet';
export const TRANSFERS_VOLUME_TO_MINERS_SUM_CODE = 'transactions.TransfersVolumeToMinersSum';
export const TRANSFERS_VOLUME_FROM_MINERS_SUM_CODE = 'transactions.TransfersVolumeFromMinersSum';
export const TRANSFERS_FROM_MINERS_COUNT_CODE = 'transactions.TransfersFromMinersCount';
export const TRANSFERS_TO_MINERS_COUNT_CODE = 'transactions.TransfersToMinersCount';
export const UTXO_PROFIT_RELATIVE_CODE = 'blockchain.UtxoProfitRelative';
export const SUPPLY_DISTRIBUTION_RELATIVE_CODE = 'addresses.SupplyDistributionRelative';

export const DIFFICULTY_CODE = 'mining.DifficultyLatest';
export const HASH_RATE_CODE = 'mining.HashRateMean';
export const MINERS_OUTFLOW_MULTIPLE_CODE = 'mining.MinersOutflowMultiple';
export const MINER_REVENUE_CODE = 'mining.RevenueSum';
export const MINER_REVENUE_FROM_FEE_CODE = 'mining.RevenueFromFees';
export const THERMOCAP_CODE = 'mining.Thermocap';
export const THERMOCAP_RATIO_CODE = 'mining.MarketcapThermocapRatio';
export const VOLUME_MINED_SUM_CODE = 'mining.VolumeMinedSum';

export const EXCHANGE_FEES_TOTAL_CODE = 'fees.ExchangesSum';
export const EXCHANGE_FEES_MEAN_CODE = 'fees.ExchangesMean';
export const EXCHANGE_FEES_RELATIVE_CODE = 'fees.ExchangesRelative';

export const EXCHANGE_FEES_GROUP = [EXCHANGE_FEES_TOTAL_CODE, EXCHANGE_FEES_MEAN_CODE, EXCHANGE_FEES_RELATIVE_CODE];

export const DERIVATIVES_GROUP = [
  DERIVATIVES_FUNDING_RATE_PERPETUAL_CODE,
  DERIVATIVES_OPEN_INTEREST_CODE,
  DERIVATIVES_OPEN_INTEREST_PERPETUAL_CODE,
  DERIVATIVES_VOLUME_DAILY_CODE,
  DERIVATIVES_VOLUME_DAILY_PERPETUAL_CODE,
];

export const DERIVATIVES_LATEST_GROUP = [DERIVATIVES_VOLUME_LATEST, DERIVATIVES_OPEN_INTEREST_LATEST];

export const SOPR_GROUP = [
  SOPR_ADJUSTED_CODE,
  SOPR_CODE,
  SOPR_LESS_155_CODE,
  SOPR_MORE_155_CODE,
  SOPR_ACCOUNT_BASED_CODE,
];
export const MVRV_GROUP = [MVRV_CODE, MVRV_MORE_155_CODE, MVRV_LESS_155_CODE, MVRV_ACCOUNT_BASED_CODE];

export const NUPL_GROUP = [
  UNREALIZED_PROFIT_LOSS_CODE,
  UNREALIZED_PROFIT_LOSS_LTH_CODE,
  UNREALIZED_PROFIT_LOSS_STH_CODE,
  NUPL_ACC_BASED_CODE,
  NUPL_LESS_155_ACC_BASED_CODE,
  NUPL_MORE_155_ACC_BASED_CODE,
];

export const HALVING_GROUP = [
  CURRENT_SUPPLY_CODE,
  DIFFICULTY_CODE,
  HASH_RATE_CODE,
  INFLATION_RATE_CODE,
  ISSUANCE_CODE,
  MINERS_OUTFLOW_MULTIPLE_CODE,
  MINER_REVENUE_CODE,
  MINER_REVENUE_FROM_FEE_CODE,
  PUELL_MULTIPLE_CODE,
  STOCK_TO_FLOW_DEFLECTION_CODE,
  STOCK_TO_FLOW_RATIO_CODE,
  THERMOCAP_CODE,
  TRANSFERS_FROM_MINERS_COUNT_CODE,
  TRANSFERS_TO_MINERS_COUNT_CODE,
  TRANSFERS_VOLUME_FROM_MINERS_SUM_CODE,
  TRANSFERS_VOLUME_MINERS_NET,
  TRANSFERS_VOLUME_TO_MINERS_SUM_CODE,
  VOLUME_MINED_SUM_CODE,
];

export const PROFIT_RELATIVE_GROUP = [
  ADDRESSES_PROFIT_RELATIVE_CODE,
  ENTITIES_PROFIT_RELATIVE_CODE,
  SUPPLY_PROFIT_RELATIVE_CODE,
];

export const DISTRIBUTION_GROUP = [SOPD_ATH_CODE, SOPD_PERCENT_CODE, URPD_ATH_CODE, URPD_PERCENT_CODE];

export const HODL_WAVES_GROUP = [HODL_WAVES_CODE, HODL_WAVES_RCAP_CODE];

export const SUPPLY_DICT = {
  more_10y: '>10y',
  '7y_10y': '7y-10y',
  '5y_7y': '5y-7y',
  '3y_5y': '3y-5y',
  '2y_3y': '2y-3y',
  '1y_2y': '1y-2y',
  '6m_12m': '6m-12m',
  '3m_6m': '3m-6m',
  '1m_3m': '1m-3m',
  '1w_1m': '1w-1m',
  '1d_1w': '1d-1w',
  '24h': '24h',
};

export const SOAB_DICT = {
  '1h': '1h',
  '1h_24h': '1h-24h',
  '1d_1w': '1d-1w',
  '1w_1m': '1w-1m',
  '1m_3m': '1m-3m',
  '3m_6m': '3m-6m',
  '6m_12m': '6m-12m',
  '1y_2y': '1y-2y',
  '2y_3y': '2y-3y',
  '3y_5y': '3y-5y',
  '5y_7y': '5y-7y',
  '7y_10y': '7y-10y',
  more_10y: '>10y',
};

export const SUPPLY_DISTRIBUTION_DICT = {
  less_0001: '< 0.0001',
  '0001_001': '0.001 - 0.01',
  '001_01': '0.01 - 0.1',
  '01_1': '0.1 - 1',
  '1_10': '1 - 10',
  '10_100': '10 - 100',
  '100_1k': '100 - 1k',
  '1k_10k': '1k - 10k',
  '10k_100k': '10k - 100k',
  above_100k: '> 100k',
};

export const SUPPLY_LTH_STH_PROFIT_LOSS_DICT = {
  sth_profit: 'STH Profit',
  sth_loss: 'STH Loss',
  lth_loss: 'LTH Loss',
  lth_profit: 'LTH Profit',
};

export const DIFFICULTY_RIBBON_DICT = {
  ma200: '200D',
  ma128: '128D',
  ma90: '90D',
  ma60: '60D',
  ma40: '40D',
  ma25: '25D',
  ma14: '14D',
  ma9: '9D',
};

export const HASH_RIBBON_DICT = {
  ma60: '60D',
  ma30: '30D',
};

export const EXCHANGE_FEES_DICT = {
  total: 'Total',
  deposit: 'Deposits',
  withdrawal: 'Withdrawals',
  inhouse: 'In-House',
  inter: 'Inter-Exchange',
};

// * = Required
// {
//   group:              (String)  GROUP NAME - used to group similar metrics together
//   *name:              (String)  FULL NAME OF THE METRIC - used by metric list
//   *shortName:         (String)  SHORT NAME OF THE METRIC - used by tooltip
//   *code:              (String)  UNIQUE IDENTIFIER - current format => metricCategory.metricAbbreviation
//   *description:       (Object)  METRIC DESCRIPTION - Descriptions per asset. Must contain a fallback default key.
//   *path:              (String)  API URL
//   *requiredParams:    (Array)   REQUIRED PARAMS FOR THE METRIC - currently used by metrics sagas to determine which params to forward to api
//   logTickInterval:    (Number)  CHART TICK INTERVAL - currently only used for log scale - refer to https://api.highcharts.com/highcharts/yAxis.tickInterval
//   currencyOption:     (Boolean) POSIBILITY TO CHANGE CURRENCY: USD / NATIVE
//   defaultCurrency:    (ENUM)    POSSIBILITY TO SET DEFAULT CURRENCY: "USD" / "NATIVE"
//   movingAverageOption (Boolean) POSIBILITY TO CHANGE MOVING AVERAGE
//   resolutionOption    (Boolean) POSIBILITY TO CHANGE RESOLUTION
//   defaultResolution   (String)  METRIC RESOLUTION - e.g. 1hr. Setting this option will always fetch metric in this resolution.
//   resolutionRestricted(Boolean) Whether higher resolution is dependant on User plan. default: true.
//   datePickerOption    (Boolean) POSIBILITY TO CHANGE DATES
//   sameAxis            (Boolean) POSSIBILITY TO PLOT METRIC/PRICE LINES ON ONE AXIS INSTEAD OF TWO
//   formatter:          (String)  DEFINES DATA POINT FORMATTER - used to format single point value, current formatters: constants/app
//
//   scales:             (Array)   SUPPORTED CHART SCALES - e.g. Linear / Logarithmic
//   defaultScale:       (STRING)  DEFAULT SCALE - e.g. Linear / Logarithmic
//   defaultPriceScale:  (STRING)  DEFAULT PRICE SCALE - e.g. Linear / Logarithmic
//
//   chartType           (String/Enum) e.g. "timeSeries"/"column"
//   compare             (Boolean)     DISPLAY METRIC IN COMPARE
//
//   useDenominations:   (Object)  Use a denomination other than token name when displaying metric values.
//     E.g. The gas metrics api returns values in Wei for Ethereum. The following config can be used to convert and display values in Gwei
//     {
//       ETH: {
//        name: 'Gwei',
//        multiplier: 0.000000001
//       }
//     }
//
//   start: {            (Object) SPECIFY METRIC START POINTS - data will not be fetched for period earlier than this.
//     BTC: [1369978729, 1369978729], // [tsForDailyResolution, tsForHourlyResolution]
//     LTC: [1369090909, 1371111111],
//   }
//
//   omitInitialDays:    (Number)  NUMBER OF INITIAL DATAPOINS TO OMIT FROM FETCHED DATA - added to remove the token distribution spike in some metrics (i.e. Number of transfers)
//
//   *tier:              (Number)  METRIC TIER LEVEL.
//   isNew:              (Boolean) NEW METRIC INDICATOR - surpasses user restriction and ads a "New" badge when true.
//
//   pulse: {            // config specific to pulse page
//     multiplier:       (Number) Works similar to useDenominations multiplier
//     formatter:        (String) Specify if different to the metric formatter
//     maxDecimals:      (Number) Max number of decimals allowed for values
//   }
//
//   upgradeOption:      (Boolean) If false, hide the upgrade banner and lag zones from chart
// }

const enhanceMetrics = (arr) => {
  return arr.map((item) => {
    const [scope, path] = item.path.split('/');
    const code = `${scope}.${upperFirst(camelCase(path))}`;

    return {
      code,
      requiredParams: [params.ASSET],

      currencyOption: false,
      exchangeOption: false,
      minerOption: false,
      movingAverageOption: true,
      movingMedianOption: true,
      scaleOption: true,
      sameAxis: false,
      resolutionOption: true,
      resolutionRestricted: true,
      datePickerOption: true,

      formatter: FORMATTER_NUMBER,
      scales: [SCALE_LIN, SCALE_LOG],
      defaultScale: SCALE_LIN,

      chartType: CHART_TYPE_TIMESERIES,
      compare: true,
      price: true,
      upgradeOption: true,

      omitInitialDays: 7,

      tier: TIER_1,
      isNew: false,

      ...item,

      pulse: {
        formatter: get(item, 'formatter', FORMATTER_NUMBER),
        ...item.pulse,
      },
    };
  });
};

export const indexesMetrics = [];

if (WITH_INTERNAL_METRICS) {
  indexesMetrics.push(
    ...enhanceMetrics([
      {
        name: '🙈Glassnode Cycle Index (GCI)',
        shortName: '🙈Glassnode Cycle Index (GCI)',
        description: {
          default: 'TODO',
        },
        path: 'indexes/gci',
        categories: [CATEGORIES.indexes, CATEGORIES.internal],
        group: 'Cycles',

        useDenominations: {
          BTC: {
            multiplier: INDEX_MULTIPLIER,
          },
        },

        tier: TIER_2,
        isNew: true,
      },
    ]),
  );
}

export const addressMetrics = enhanceMetrics([
  {
    name: 'Number of New Addresses',
    shortName: 'New Addresses',
    description: {
      default:
        'The number of unique addresses that appeared for the first time in a transaction of the native coin in the network.',
    },
    path: 'addresses/new_non_zero_count',
    categories: [CATEGORIES.addresses, CATEGORIES.networkStats],
    group: 'Address Growth',
  },
  {
    name: 'Number of Active Addresses',
    shortName: 'Active Addresses',
    description: {
      default:
        'The number of unique addresses that were active in the network either as a sender or receiver. Only addresses that were active in successful transactions are counted.',
    },
    path: 'addresses/active_count',
    categories: [CATEGORIES.addresses, CATEGORIES.networkStats],
    group: 'Address Activity',
  },
  {
    name: 'Number of Sending Addresses',
    shortName: 'Sending Addresses',
    description: {
      default:
        'The number of unique addresses that were active as a sender of funds. Only addresses that were active as a sender in successful non-zero transfers are counted.',
    },
    path: 'addresses/sending_count',
    categories: [CATEGORIES.addresses, CATEGORIES.networkStats],
    group: 'Address Activity',
  },
  {
    name: 'Number of Receiving Addresses',
    shortName: 'Receiving Addresses',
    description: {
      default:
        'The number of unique addresses that were active as a receiver of funds. Only addresses that were active as a receiver in successful non-zero transfers are counted.',
    },
    path: 'addresses/receiving_count',
    categories: [CATEGORIES.addresses, CATEGORIES.networkStats],
    group: 'Address Activity',
  },
  {
    name: 'Number of Addresses',
    shortName: 'Total Addresses',
    description: {
      default:
        'The total number of unique addresses that ever appeared in a transaction of the native coin in the network.',
    },
    path: 'addresses/count',
    categories: [CATEGORIES.addresses, CATEGORIES.networkStats],
    group: 'Address Growth',
  },
  {
    name: 'Number of Addresses with a Non-Zero Balance',
    shortName: 'Addresses with Non-Zero Balance',
    description: {
      default: 'The number of unique addresses holding a positive (non-zero) amount of coins.',
      ETH:
        'The number of unique addresses holding a positive (non-zero) amount of coins. Contracts are excluded, only Externally Owned Addresses (EOAs) are counted.',
    },
    path: 'addresses/non_zero_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 0.01',
    shortName: 'Addresses with Balance ≥ 0.01',
    description: {
      default: 'The number of unique addresses holding at least 0.01 coins.',
      ETH:
        'The number of unique addresses holding at least 0.01 coins. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_point_zero_1_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 0.1',
    shortName: 'Addresses with Balance ≥ 0.1',
    description: {
      default: 'The number of unique addresses holding at least 0.1 coins.',
      ETH:
        'The number of unique addresses holding at least 0.1 coins. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_point_1_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 1',
    shortName: 'Addresses with Balance ≥ 1',
    description: {
      default: 'The number of unique addresses holding at least 1 coin.',
      ETH:
        'The number of unique addresses holding at least 1 coin. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_1_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 10',
    shortName: 'Addresses with Balance ≥ 10',
    description: {
      default: 'The number of unique addresses holding at least 10 coins.',
      ETH:
        'The number of unique addresses holding at least 10 coins. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_10_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 100',
    shortName: 'Addresses with Balance ≥ 100',
    description: {
      default: 'The number of unique addresses holding at least 100 coins.',
      ETH:
        'The number of unique addresses holding at least 100 coins. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_100_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 1k',
    shortName: 'Addresses with Balance ≥ 1k',
    description: {
      default: 'The number of unique addresses holding at least 1k coins.',
      ETH:
        'The number of unique addresses holding at least 1k coins. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_1k_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 10k',
    shortName: 'Addresses with Balance ≥ 10k',
    description: {
      default: 'The number of unique addresses holding at least 10k coins.',
      ETH:
        'The number of unique addresses holding at least 10k coins. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_10k_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ 32 ETH',
    shortName: 'Addresses with Balance ≥ 32 ETH',
    description: {
      default:
        'The number of unique addresses holding at least 32 ETH. This is the number of potential validators for ETH 2.0. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_32_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (Native)',

    tier: TIER_2,
  },
  {
    name: 'Number of Addresses with Balance ≥ $1',
    shortName: 'Addresses with Balance ≥ $1',
    description: {
      default: 'The number of unique addresses holding at least a value of $1 USD.',
      ETH:
        'The number of unique addresses holding at least a value of $1 USD. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_1_usd_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (USD)',

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses with Balance ≥ $10',
    shortName: 'Addresses with Balance ≥ $10',
    description: {
      default: 'The number of unique addresses holding at least a value of $10 USD.',
      ETH:
        'The number of unique addresses holding at least a value of $10 USD. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_10_usd_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (USD)',

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses with Balance ≥ $100',
    shortName: 'Addresses with Balance ≥ $100',
    description: {
      default: 'The number of unique addresses holding at least a value of $100 USD.',
      ETH:
        'The number of unique addresses holding at least a value of $100 USD. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_100_usd_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (USD)',

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses with Balance ≥ $1k',
    shortName: 'Addresses with Balance ≥ $1k',
    description: {
      default: 'The number of unique addresses holding at least a value of $1k USD.',
      ETH:
        'The number of unique addresses holding at least a value of $1k USD. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_1k_usd_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (USD)',

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses with Balance ≥ $10k',
    shortName: 'Addresses with Balance ≥ $10k',
    description: {
      default: 'The number of unique addresses holding at least a value of $10k USD.',
      ETH:
        'The number of unique addresses holding at least a value of $10k USD. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_10k_usd_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (USD)',

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses with Balance ≥ $100k',
    shortName: 'Addresses with Balance ≥ $100k',
    description: {
      default: 'The number of unique addresses holding at least a value of $100k USD.',
      ETH:
        'The number of unique addresses holding at least a value of $100k USD. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_100k_usd_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (USD)',

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses with Balance ≥ $1M',
    shortName: 'Addresses with Balance ≥ $1M',
    description: {
      default: 'The number of unique addresses holding at least a value of $1M USD.',
      ETH:
        'The number of unique addresses holding at least a value of $1M USD. Only Externally Owned Addresses (EOAs) are counted, contracts are excluded.',
    },
    path: 'addresses/min_1m_usd_count',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution],
    group: 'Address Balances (USD)',

    tier: TIER_3,
  },
  {
    name: 'Percent of Addresses in Profit',
    shortName: 'Percent Addresses in Profit',
    description: {
      default: `The percentage of unique addresses whose funds have an average buy price that is lower than the current price. "Buy price" is here defined as the price at the time coins were transferred into an address.`,
      ETH: `The percentage of unique addresses whose funds have an average buy price that is lower than the current price. "Buy price" is here defined as the price at the time coins were transferred into an address. Only Externally Owned Addresses (EOAs) is are counted, contracts are excluded.`,
    },
    path: 'addresses/profit_relative',
    categories: [CATEGORIES.addresses, CATEGORIES.profitLoss],
    group: 'Addresses in Profit/Loss',

    start: {
      BTC: [1280222701, 1280222701],
    },
    formatter: FORMATTER_PERCENT,

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses in Profit',
    shortName: 'Addresses in Profit',
    description: {
      default: `The number of unique addresses whose funds have an average buy price that is lower than the current price. "Buy price" is here defined as the price at the time coins were transferred into an address.`,
      ETH: `The number of unique addresses whose funds have an average buy price that is lower than the current price. "Buy price" is here defined as the price at the time coins were transferred into an address. Only Externally Owned Addresses (EOAs) is are counted, contracts are excluded.`,
    },
    path: 'addresses/profit_count',
    categories: [CATEGORIES.addresses, CATEGORIES.profitLoss],
    group: 'Addresses in Profit/Loss',

    tier: TIER_3,
  },
  {
    name: 'Number of Addresses in Loss',
    shortName: 'Addresses in Loss',
    description: {
      default: `The number of unique addresses whose funds have an average buy price that is higher than the current price. "Buy price" is here defined as the price at the time coins were transferred into an address.`,
      ETH: `The number of unique addresses whose funds have an average buy price that is higher than the current price. "Buy price" is here defined as the price at the time coins were transferred into an address. Only Externally Owned Addresses (EOAs) is are counted, contracts are excluded.`,
    },
    path: 'addresses/loss_count',
    categories: [CATEGORIES.addresses, CATEGORIES.profitLoss],
    group: 'Addresses in Profit/Loss',

    tier: TIER_3,
  },
  {
    name: 'Relative Address Supply Distribution',
    shortName: 'Address Supply Distribution',
    description: {
      default: 'The relative distribution of the circulating supply held by addresses with specific balance bands.',
      ETH:
        'The relative supply distribution across addresses with specific balance bands. Only supply in Externally Owned Addresses (EOAs) is included, supply in contracts is excluded. Therefore the numbers are relative to the total supply held in EOAs.',
    },
    path: 'addresses/supply_distribution_relative',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    formatter: FORMATTER_PERCENT,
    movingAverageOption: false,
    movingMedianOption: false,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance < 0.001',
    shortName: 'Supply Held by Addresses with Balance < 0.001',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance lower than 0.001 coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance lower than 0.001 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_less_0001',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 0.001 - 0.01',
    shortName: 'Supply Held by Addresses with Balance 0.001 - 0.01',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 0.001 and 0.01 coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 0.001 and 0.01 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_0001_001',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 0.01 - 0.1',
    shortName: 'Supply Held by Addresses with Balance 0.01 - 0.1',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 0.01 and 0.1 coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 0.01 and 0.1 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_001_01',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 0.1 - 1',
    shortName: 'Supply Held by Addresses with Balance 0.1 - 1',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 0.1 and 1 coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 0.1 and 1 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_01_1',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 1 - 10',
    shortName: 'Supply Held by Addresses with Balance 1 - 10',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 1 and 10 coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 1 and 10 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_1_10',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 10 - 100',
    shortName: 'Supply Held by Addresses with Balance 10 - 100',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 10 and 100 coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 10 and 100 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_10_100',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 100 - 1k',
    shortName: 'Supply Held by Addresses with Balance 100 - 1k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 100 and 1k coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 100 and 1,000 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_100_1k',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 1k - 10k',
    shortName: 'Supply Held by Addresses with Balance 1k - 10k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 1k and 10k coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 1,000 and 10,000 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_1k_10k',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance 10k - 100k',
    shortName: 'Supply Held by Addresses with Balance 10k - 100k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 10k and 100k coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance between 10,000 and 100,000 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_10k_100k',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Addresses with Balance > 100k',
    shortName: 'Supply Held by Addresses with Balance > 100k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by addresses with a balance of at least 100,000 coins.`,
      ETH: `The total supply${CURRENCY_PLACEHOLDER} held by addresses with a balance of at least 100,000 coins. Only supply in Externally Owned Addresses (EOAs) is included, contracts are excluded.`,
    },
    path: 'addresses/supply_balance_more_100k',
    categories: [CATEGORIES.addresses, CATEGORIES.distribution, CATEGORIES.supply],
    group: 'Address Supply',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Number of Accumulation Addresses',
    shortName: 'Accumulation Addresses',
    description: {
      default: `The number of unique accumulation addresses. Accumulation addresses are defined as addresses that have at least 2 incoming non-dust transfers and have never spent funds. Exchange addresses and addresses receiving from coinbase transactions (miner addresses) are discarded. To account for lost coins, addresses that were last active more than 7 years ago are omitted as well.`,
    },
    path: 'addresses/accumulation_count',
    categories: [CATEGORIES.distribution, CATEGORIES.addresses, CATEGORIES.longShortTermHolders],
    group: 'Hodlers',

    tier: TIER_3,
    isNew: true,
  },
  {
    name: 'Total Balance in Accumulation Addresses',
    shortName: 'Accumulation Balance',
    description: {
      default: `The total amount of funds${CURRENCY_PLACEHOLDER} held in [accumulation addresses](studio.glassnode.com/metrics?a=BTC&m=addresses.AccumulationCount). Accumulation addresses are defined as addresses that have at least 2 incoming non-dust transfers and have never spent funds. Exchange addresses and addresses receiving from coinbase transactions (miner addresses) are discarded. To account for lost coins, addresses that were last active more than 7 years ago are omitted as well.`,
    },
    path: 'addresses/accumulation_balance',
    categories: [CATEGORIES.distribution, CATEGORIES.addresses, CATEGORIES.longShortTermHolders],
    group: 'Hodlers',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
    isNew: true,
  },
]);

if (WITH_INTERNAL_METRICS) {
  addressMetrics.push(
    ...enhanceMetrics([
      {
        name: '🙈Number of Addresses Depositing to Exchanges',
        shortName: '🙈Depositing Addresses',
        description: {
          default: `The number of unique addresses that appeared as a sender in a transaction sending funds to exchanges.`,
        },
        path: 'addresses/sending_to_exchanges_count',
        categories: [CATEGORIES.addresses, CATEGORIES.exchanges, CATEGORIES.internal, CATEGORIES.internal],
        group: 'Exchange Activity',

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Number of Addresses Withdrawing from Exchanges',
        shortName: '🙈Withdrawing Addresses',
        description: {
          default: `The number of unique addresses that appeared as a receiver in a transaction receiving funds from an exchanges.`,
        },
        path: 'addresses/receiving_from_exchanges_count',
        categories: [CATEGORIES.addresses, CATEGORIES.exchanges, CATEGORIES.internal, CATEGORIES.internal],
        group: 'Exchange Activity',

        tier: TIER_3,
        isNew: true,
      },
    ]),
  );
}

export const exchangeMetrics = enhanceMetrics([
  {
    name: 'Total Transfer Volume to Exchanges',
    shortName: 'Exchange Inflow Volume (Total)',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred to exchange addresses. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
    },
    path: 'transactions/transfers_volume_to_exchanges_sum',
    categories: [CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.transactions, CATEGORIES.volume],
    group: 'Exchange Activity',

    requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
    exchangeOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Transfer Volume from Exchanges',
    shortName: 'Exchange Outflow Volume (Total)',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred from exchange addresses. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
    },
    path: 'transactions/transfers_volume_from_exchanges_sum',
    categories: [CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.transactions, CATEGORIES.volume],
    group: 'Exchange Activity',

    requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
    exchangeOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Net Transfer Volume from/to Exchanges',
    shortName: 'Exchange Net Flow Volume',
    description: {
      default: `The difference of in volume${CURRENCY_PLACEHOLDER} flowing into exchanges and out of exchanges, i.e. the net flow of coins into/out of exchanges. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
    },
    path: 'transactions/transfers_volume_exchanges_net',
    categories: [CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.transactions, CATEGORIES.volume],
    group: 'Exchange Activity',

    requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
    exchangeOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
    scales: [SCALE_LIN],

    tier: TIER_2,
  },
  {
    name: 'Balance on Exchanges',
    shortName: 'Exchange Balance (Stacked)',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} held on exchange addresses. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
    },
    path: 'distribution/balance_exchanges_all',
    categories: [CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.distribution],
    group: 'Entity Balances',

    scales: [SCALE_LIN],
    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
    movingAverageOption: false,
    movingMedianOption: false,
    compare: false,

    tier: TIER_2,
  },
  {
    name: 'Balance on Exchanges',
    shortName: 'Exchange Balance',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} held on exchange addresses. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
    },
    path: 'distribution/balance_exchanges',
    categories: [CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
    exchangeOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Transfer Volume from Miners to Exchanges',
    shortName: 'Miners to Exchanges (Stacked)',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred from miners to exchange wallets. Only direct transfers are counted. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
    },
    path: 'transactions/transfers_volume_miners_to_exchanges_all',
    categories: [
      CATEGORIES.entities,
      CATEGORIES.exchanges,
      CATEGORIES.miners,
      CATEGORIES.transactions,
      CATEGORIES.volume,
    ],
    group: 'Miner Activity',

    requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
    formatter: FORMATTER_CURRENCY,
    currencyOption: true,
    exchangeOption: true,
    compare: false,
    scales: [SCALE_LIN],

    tier: TIER_3,
  },
  {
    name: 'Transfer Volume from Miners to Exchanges',
    shortName: 'Miners to Exchanges',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred from miners to exchange wallets. Only direct transfers are counted. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
    },
    path: 'transactions/transfers_volume_miners_to_exchanges',
    categories: [
      CATEGORIES.entities,
      CATEGORIES.exchanges,
      CATEGORIES.miners,
      CATEGORIES.transactions,
      CATEGORIES.volume,
    ],
    group: 'Miner Activity',

    requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
    exchangeOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Number of Transfers to Exchanges',
    shortName: 'Exchange Deposits',
    description: {
      default:
        'The total count of transfers to exchange addresses, i.e. the number of on-chain deposits to exchanges. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.',
    },
    path: 'transactions/transfers_to_exchanges_count',
    categories: [CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.transactions],
    group: 'Exchange Activity',

    requiredParams: [params.ASSET, params.EXCHANGE],
    exchangeOption: true,

    tier: TIER_2,
  },
  {
    name: 'Number of Transfers from Exchanges',
    shortName: 'Exchange Withdrawals',
    description: {
      default:
        'The total count of transfers from exchange addresses, i.e. the number of on-chain withdrawals from exchanges. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.',
    },
    path: 'transactions/transfers_from_exchanges_count',
    categories: [CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.transactions],
    group: 'Exchange Activity',

    requiredParams: [params.ASSET, params.EXCHANGE],
    exchangeOption: true,

    tier: TIER_2,
  },
]);

if (WITH_INTERNAL_METRICS) {
  exchangeMetrics.push(
    ...enhanceMetrics([
      {
        name: '🙈Total Transfer Volume Within Exchanges',
        shortName: '🙈 In-House Exchange Volume',
        description: {
          default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred within wallets of the same exchange. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
        },
        path: 'transactions/transfers_volume_within_exchanges_sum',
        categories: [
          CATEGORIES.entities,
          CATEGORIES.exchanges,
          CATEGORIES.transactions,
          CATEGORIES.volume,
          CATEGORIES.internal,
        ],
        group: 'Exchange Activity',

        requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
        exchangeOption: true,
        currencyOption: true,
        formatter: FORMATTER_CURRENCY,

        tier: TIER_2,
        isNew: true,
      },
      {
        name: '🙈Mean Transfer Volume to Exchanges',
        shortName: '🙈Exchange Inflow Volume (Mean)',
        description: {
          default: `The mean value${CURRENCY_PLACEHOLDER} of a transfer to exchanges addresses. Only successful transfers are counted. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
        },
        path: 'transactions/transfers_volume_to_exchanges_mean',
        categories: [
          CATEGORIES.entities,
          CATEGORIES.exchanges,
          CATEGORIES.transactions,
          CATEGORIES.volume,
          CATEGORIES.internal,
        ],
        group: 'Exchange Activity',

        requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
        exchangeOption: true,
        currencyOption: true,
        formatter: FORMATTER_CURRENCY,

        tier: TIER_2,
        isNew: true,
      },
      {
        name: '🙈Mean Transfer Volume from Exchanges',
        shortName: '🙈Exchange Outflow Volume (Mean)',
        description: {
          default: `The mean value${CURRENCY_PLACEHOLDER} of a transfer from exchanges addresses. Only successful transfers are counted. Note that exchange metrics are based on our labeled data of exchange addresses that we constantly keep updating, as well as data science techniques and statistical information that changes over time. Therefore these metrics are mutable – the data is stable, but especially most recent data points are subject to slight fluctuations as time progresses.`,
        },
        path: 'transactions/transfers_volume_from_exchanges_mean',
        categories: [
          CATEGORIES.entities,
          CATEGORIES.exchanges,
          CATEGORIES.transactions,
          CATEGORIES.volume,
          CATEGORIES.internal,
        ],
        group: 'Exchange Activity',

        requiredParams: [params.ASSET, params.CURRENCY, params.EXCHANGE],
        exchangeOption: true,
        currencyOption: true,
        formatter: FORMATTER_CURRENCY,

        tier: TIER_2,
        isNew: true,
      },
    ]),
  );
}

export const feesMetrics = enhanceMetrics([
  {
    name: 'Total Transaction Fees',
    shortName: 'Fees (Total)',
    description: {
      default: `The total amount of fees${CURRENCY_PLACEHOLDER} paid to miners. Issued (minted) coins are not included.`,
    },
    path: 'fees/volume_sum',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Fees',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Mean Transaction Fees',
    shortName: 'Fees (Mean)',
    description: {
      default: `The mean fee${CURRENCY_PLACEHOLDER} per transaction. Issued (minted) coins are not included.`,
    },
    path: 'fees/volume_mean',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Fees',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Fee Ratio Multiple (FRM)',
    shortName: 'Fee Ratio Multiple (FRM)',
    description: {
      default: `The Fee Ratio Multiple (FRM) is defined as the ratio between the total miner revenue (blocks rewards + transaction fees) and transaction fees. FRM is a measure of a blockchain's security and gives an assessment how secure a chain is once block rewards disappear. This metric was first introduced by [Matteo Leibowitz](https://twitter.com/teo_leibowitz). For more information please see his [article](https://medium.com/coinmonks/introducing-fee-ratio-multiple-frm-1eada9ac9bec).`,
    },
    path: 'fees/fee_ratio_multiple',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.ratios],
    group: 'Fees',

    start: {
      BTC: [1356912000, 1356912000],
      LTC: [1368921600, 1368921600],
    },

    tier: TIER_2,
  },
  {
    name: 'Total Gas Used',
    shortName: 'Gas Used (Total)',
    description: { default: 'The total amount of gas used in all transactions.' },
    path: 'fees/gas_used_sum',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Gas',
  },
  {
    name: 'Mean Gas Used',
    shortName: 'Gas Used (Mean)',
    description: { default: 'The mean amount of gas used per transaction.' },
    path: 'fees/gas_used_mean',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Gas',
  },
  {
    name: 'Median Gas Used',
    shortName: 'Gas Used (Median)',
    description: { default: 'The median amount of gas used per transaction.' },
    path: 'fees/gas_used_median',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Gas',
  },
  {
    name: 'Mean Transaction Gas Price',
    shortName: 'Gas Price (Mean)',
    description: { default: `The mean gas price${CURRENCY_PLACEHOLDER} paid per transaction.` },
    path: 'fees/gas_price_mean',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Gas',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
    useDenominations: {
      ETH: {
        name: 'Gwei',
        multiplier: WEI_TO_GWEI_MULTIPLIER,
      },
    },
  },
  {
    name: 'Median Transaction Gas Price',
    shortName: 'Gas Price (Median)',
    description: { default: `The median gas price${CURRENCY_PLACEHOLDER} paid per transaction.` },
    path: 'fees/gas_price_median',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Gas',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
    useDenominations: {
      ETH: {
        name: 'Gwei',
        multiplier: WEI_TO_GWEI_MULTIPLIER,
      },
    },
  },
  {
    name: 'Mean Transaction Gas Limit',
    shortName: 'Transaction Gas Limit (Mean)',
    description: { default: 'The mean gas limit per transaction.' },
    path: 'fees/gas_limit_tx_mean',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Gas',
  },
  {
    name: 'Median Transaction Gas Limit',
    shortName: 'Transaction Gas Limit (Median)',
    description: { default: 'The median gas limit per transaction.' },
    path: 'fees/gas_limit_tx_median',
    categories: [CATEGORIES.fees, CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Gas',
  },
  {
    name: 'Total Exchange Transaction Fees',
    shortName: 'Exchange Fees (Total)',
    description: {
      default: `The total amount of fees${CURRENCY_PLACEHOLDER} paid in transactions related to on-chain exchange activity.
- Deposits: Transactions that include an exchange address as the receiver of funds.
- Withdrawals: Transactions that include an exchange address as the sender of funds.
- In-House: Transactions that include addresses of a single exchange as both the sender and receiver of funds.
- Inter-Exchange: Transactions that include addresses of (distinct) exchanges as both the sender and receiver of funds.

If a transaction can be categorized into multiple of these categories (e.g. a transaction that sends funds externally as well as in-house), the fees are split into percentages according to the volume transferred.`,
    },
    path: 'fees/exchanges_sum',
    categories: [CATEGORIES.fees, CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.networkStats],
    group: 'Fees',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
    scales: [SCALE_LIN],
    compare: false,
    resolutionOption: false,

    tier: TIER_3,
    isNew: true,
  },
  {
    name: 'Mean Exchange Transaction Fees',
    shortName: 'Exchange Fees (Mean)',
    description: {
      default: `The mean amount of fees${CURRENCY_PLACEHOLDER} paid in transactions related to on-chain exchange activity. Note that the mean is computed over transfers, not transactions.
- Deposits: Transactions that include an exchange address as the receiver of funds.
- Withdrawals: Transactions that include an exchange address as the sender of funds.
- In-House: Transactions that include addresses of a single exchange as both the sender and receiver of funds.
- Inter-Exchange: Transactions that include addresses of (distinct) exchanges as both the sender and receiver of funds.

If a transaction can be categorized into multiple of these categories (e.g. a transaction that sends funds externally as well as in-house), the fees are split into percentages according to the volume transferred.`,
    },
    path: 'fees/exchanges_mean',
    categories: [CATEGORIES.fees, CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.networkStats],
    group: 'Fees',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
    compare: false,
    resolutionOption: false,

    tier: TIER_3,
    isNew: true,
  },
  {
    name: 'Exchange Fee Dominance',
    shortName: 'Exchange Fee Dominance',
    description: {
      default: `The Exchange Fee Dominance metric is defined as the percent amount of total fees paid in transactions related to on-chain exchange activity.
- Deposits: Transactions that include an exchange address as the receiver of funds.
- Withdrawals: Transactions that include an exchange address as the sender of funds.
- In-House: Transactions that include addresses of a single exchange as both the sender and receiver of funds.
- Inter-Exchange: Transactions that include addresses of (distinct) exchanges as both the sender and receiver of funds.

If a transaction can be categorized into multiple of these categories (e.g. a transaction that sends funds externally as well as in-house), the fees are split into percentages according to the volume transferred.`,
    },
    path: 'fees/exchanges_relative',
    categories: [CATEGORIES.fees, CATEGORIES.entities, CATEGORIES.exchanges, CATEGORIES.networkStats],
    group: 'Fees',

    formatter: FORMATTER_PERCENT,
    scales: [SCALE_LIN],
    compare: false,
    resolutionOption: false,

    tier: TIER_3,
    isNew: true,
  },
]);

export const transactionMetrics = enhanceMetrics([
  {
    name: 'Number of Transactions',
    shortName: 'Transaction Count',
    description: { default: 'The total amount of transactions. Only successful transactions are counted.' },
    path: 'transactions/count',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Transaction Count',
  },
  {
    name: 'Number of Transactions per Second',
    shortName: 'Transaction Rate',
    description: { default: 'The total amount of transactions per second. Only successful transactions are counted.' },
    path: 'transactions/rate',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Transaction Count',
  },
  {
    name: 'Number of Transfers',
    shortName: 'Transfer Count',
    description: {
      default:
        'The total amount of transfers. One transaction can trigger one or more transfers. Only successful, non-zero transfers are counted.',
    },
    path: 'transactions/transfers_count',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Transaction Count',
  },
  {
    name: 'Number of Transfers per Second',
    shortName: 'Transfer Rate',
    description: {
      default:
        'The total amount of transfers per second. One transaction can trigger one or more transfers. Only successful, non-zero transfers are counted.',
    },
    path: 'transactions/transfers_rate',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Transaction Count',
  },
  {
    name: 'Total Size of Transactions',
    shortName: 'Transaction Size (Total)',
    description: { default: 'The total size of all transactions within the time period (in bytes).' },
    path: 'transactions/size_sum',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Transaction Size',
  },
  {
    name: 'Mean Size of Transfers',
    shortName: 'Transaction Size (Mean)',
    description: { default: 'The mean size of a transaction within the time period (in bytes).' },
    path: 'transactions/size_mean',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Transaction Size',

    start: {
      BTC: [1279637355, 1282671300],
    },
  },
  {
    name: 'Total Transfer Volume',
    shortName: 'Transfer Volume (Total)',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred on-chain. Only successful transfers are counted.`,
    },
    path: 'transactions/transfers_volume_sum',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Mean Transfer Volume',
    shortName: 'Transfer Volume (Mean)',
    description: {
      default: `The mean value${CURRENCY_PLACEHOLDER} of a transfer. Only successful transfers are counted.`,
    },
    path: 'transactions/transfers_volume_mean',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Median Transfer Volume',
    shortName: 'Transfer Volume (Median)',
    description: {
      default: `The median value${CURRENCY_PLACEHOLDER} of a transfer. Only successful transfers are counted.`,
    },
    path: 'transactions/transfers_volume_median',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Total Transfer Volume (Change-Adjusted)',
    shortName: 'Change-Adjusted Volume (Total)',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred on-chain, adjusted by change volume. Only successful transfers are counted.`,
    },
    path: 'transactions/transfers_volume_adjusted_sum',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Mean Transfer Volume (Change-Adjusted)',
    shortName: 'Change-Adjusted Volume (Mean)',
    description: {
      default: `The mean value${CURRENCY_PLACEHOLDER} of a transfer, adjusted by change volume. Only successful transfers are counted.`,
    },
    path: 'transactions/transfers_volume_adjusted_mean',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Median Transfer Volume (Change-Adjusted)',
    shortName: 'Change-Adjusted Volume (Median)',
    description: {
      default: `The median value${CURRENCY_PLACEHOLDER} of a transfer, adjusted by change volume. Only successful transfers are counted.`,
    },
    path: 'transactions/transfers_volume_adjusted_median',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Number of Transactions (Entity-Adjusted)',
    shortName: 'Entity-Adjusted Transaction Count',
    description: {
      default: `The estimated entity-adjusted number of transactions is defined as the number of transactions between different entities, i.e. the total number of transactions excluding transactions within addresses of the same entity. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses.\n\nFor more information [this article](https://insights.glassnode.com/true-bitcoin-volume).`,
    },
    path: 'transactions/entity_adjusted_count',
    categories: [CATEGORIES.transactions, 'Entities', CATEGORIES.networkStats],
    group: 'Transaction Count',

    tier: TIER_3,
  },
  {
    name: 'Total Transfer Volume (Entity-Adjusted)',
    shortName: 'Entity-Adjusted Volume (Total)',
    description: {
      default: `The total estimated amount of coins${CURRENCY_PLACEHOLDER} moved between different entities, i.e. the total volumed transferred excluding the volume transferred within addresses of the same entity. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses.\n\nFor more information [this article](https://insights.glassnode.com/true-bitcoin-volume).`,
    },
    path: 'transactions/transfers_volume_entity_adjusted_sum',
    categories: [CATEGORIES.transactions, 'Entities', CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Mean Transfer Volume (Entity-Adjusted)',
    shortName: 'Entity-Adjusted Volume (Mean)',
    description: {
      default: `The mean estimated amount of coins${CURRENCY_PLACEHOLDER} moved between different entities, i.e. excluding the volume transferred within addresses of the same entity. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses.\n\nFor more information [this article](https://insights.glassnode.com/true-bitcoin-volume).`,
    },
    path: 'transactions/transfers_volume_entity_adjusted_mean',
    categories: [CATEGORIES.transactions, 'Entities', CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Median Transfer Volume (Entity-Adjusted)',
    shortName: 'Entity-Adjusted Volume (Median)',
    description: {
      default: `The median estimated amount of coins${CURRENCY_PLACEHOLDER} moved between different entities, i.e. excluding volume transferred within addresses of the same entity. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses.\n\nFor more information [this article](https://insights.glassnode.com/true-bitcoin-volume).`,
    },
    path: 'transactions/transfers_volume_entity_adjusted_median',
    categories: [CATEGORIES.transactions, 'Entities', CATEGORIES.networkStats, CATEGORIES.volume],
    group: 'Transfer Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Total Number of Contract Calls (External)',
    shortName: 'Contract Calls (External)',
    description: {
      default:
        'The total number of smart contract calls on the Ethereum network initiated by Externally Owned Addresses (EOAs), i.e. external transactions. Only successful transactions are counted.',
    },
    path: 'transactions/contract_calls_external_count',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Smart Contracts',

    isNew: true,
    tier: TIER_2,
  },
  {
    name: 'Total Number of Contract Calls (Internal)',
    shortName: 'Contract Calls (Internal)',
    description: {
      default:
        'The total number of internal smart contract calls on the Ethereum network, i.e. contract calls initiated by other contracts (internal transactions). Only successful transactions are counted.',
    },
    path: 'transactions/contract_calls_internal_count',
    categories: [CATEGORIES.transactions, CATEGORIES.networkStats],
    group: 'Smart Contracts',

    isNew: true,
    tier: TIER_2,
  },
]);

if (WITH_INTERNAL_METRICS) {
  transactionMetrics.push(
    ...enhanceMetrics([
      {
        name: '🙈Percent of Transfer Volume in Profit',
        shortName: '🙈Percent Volume in Profit',
        description: {
          default:
            'The percentage of transfer volume in profit, i.e. the percentage of transferred coins whose price at the time of their previous movement was lower than the current price. Spent outputs with a lifespan of less than an hour are discarded.',
        },
        path: 'transactions/transfers_volume_profit_relative',
        categories: [CATEGORIES.transactions, CATEGORIES.profitLoss, CATEGORIES.volume, CATEGORIES.internal],
        group: 'Transfer Volume in Profit/Loss',

        formatter: FORMATTER_PERCENT,

        isNew: true,
        tier: TIER_3,
      },
      {
        name: '🙈Total Transfer Volume in Profit',
        shortName: '🙈Transfer Volume in Profit',
        description: {
          default: `The total transfer volume in profit${CURRENCY_PLACEHOLDER}, i.e. the amount of transferred coins whose price at the time of their previous movement was lower than the price during the current transfer. Spent outputs with a lifespan of less than an hour are discarded.`,
        },
        path: 'transactions/transfers_volume_profit_sum',
        categories: [CATEGORIES.transactions, CATEGORIES.profitLoss, CATEGORIES.volume, CATEGORIES.internal],
        group: 'Transfer Volume in Profit/Loss',

        requiredParams: [params.ASSET, params.CURRENCY],
        formatter: FORMATTER_CURRENCY,
        currencyOption: true,

        isNew: true,
        tier: TIER_3,
      },
      {
        name: '🙈Total Transfer Volume in Loss',
        shortName: '🙈Transfer Volume in Loss',
        description: {
          default: `The total transfer volume in loss${CURRENCY_PLACEHOLDER}, i.e. the amount of transferred coins whose price at the time of their previous movement was higher than the price during the current transfer. Spent outputs with a lifespan of less than an hour are discarded.`,
        },
        path: 'transactions/transfers_volume_loss_sum',
        categories: [CATEGORIES.transactions, CATEGORIES.profitLoss, CATEGORIES.volume, CATEGORIES.internal],
        group: 'Transfer Volume in Profit/Loss',

        requiredParams: [params.ASSET, params.CURRENCY],
        formatter: FORMATTER_CURRENCY,
        currencyOption: true,

        isNew: true,
        tier: TIER_3,
      },
    ]),
  );
}

export const utxoMetrics = enhanceMetrics([
  {
    name: 'Percent of UTXOs in Profit',
    shortName: 'Percent UTXOs in Profit',
    description: {
      default:
        'The percentage of unspent transaction outputs whose price at creation time was lower than the current price. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).',
    },
    path: 'blockchain/utxo_profit_relative',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.profitLoss],
    group: 'UTXOs in Profit/Loss',

    scales: [SCALE_LIN],
    formatter: FORMATTER_PERCENT,

    tier: TIER_2,
  },
  {
    name: 'Number of UTXOs in Profit',
    shortName: 'UTXOs in Profit',
    description: {
      default:
        'The number of unspent transaction outputs whose price at creation time was lower than the current price. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).',
    },
    path: 'blockchain/utxo_profit_count',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.profitLoss],
    group: 'UTXOs in Profit/Loss',

    tier: TIER_2,
  },
  {
    name: 'Number of UTXOs in Loss',
    shortName: 'UTXOs in Loss',
    description: {
      default:
        'The number of unspent transaction outputs whose price at creation time was higher than the current price. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).',
    },
    path: 'blockchain/utxo_loss_count',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.profitLoss],
    group: 'UTXOs in Profit/Loss',

    tier: TIER_2,
  },
  {
    name: 'Number of Created UTXOs',
    shortName: 'UTXOs Created',
    description: { default: 'The number of created unspent transaction outputs.' },
    path: 'blockchain/utxo_created_count',
    categories: [CATEGORIES.unspentSpentOutputs],
    group: 'UTXO Count',
  },
  {
    name: 'Number of Spent UTXOs',
    shortName: 'UTXOs Spent',
    description: { default: 'The number of spent transaction outputs.' },
    path: 'blockchain/utxo_spent_count',
    categories: [CATEGORIES.unspentSpentOutputs],
    group: 'UTXO Count',
  },
  {
    name: 'Total Value of Created UTXOs',
    shortName: 'UTXO Value Created (Total)',
    description: { default: `The total amount of coins${CURRENCY_PLACEHOLDER} in newly created UTXOs.` },
    path: 'blockchain/utxo_created_value_sum',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.volume],
    group: 'UTXO Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Mean Value of Created UTXOs',
    shortName: 'UTXO Value Created (Mean)',
    description: { default: `The mean amount of coins${CURRENCY_PLACEHOLDER} in newly created UTXOs.` },
    path: 'blockchain/utxo_created_value_mean',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.volume],
    group: 'UTXO Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Median Value of Created UTXOs',
    shortName: 'UTXO Value Created (Median)',
    description: { default: `The median amount of coins${CURRENCY_PLACEHOLDER} in newly created UTXOs.` },
    path: 'blockchain/utxo_created_value_median',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.volume],
    group: 'UTXO Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Total Value of Spent UTXOs',
    shortName: 'UTXO Value Spent (Total)',
    description: { default: `The total amount of coins${CURRENCY_PLACEHOLDER} in spent transaction outputs.` },
    path: 'blockchain/utxo_spent_value_sum',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.volume],
    group: 'UTXO Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Mean Value of Spent UTXOs',
    shortName: 'UTXO Value Spent (Mean)',
    description: { default: `The mean amount of coins${CURRENCY_PLACEHOLDER} in spent transaction outputs.` },
    path: 'blockchain/utxo_spent_value_mean',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.volume],
    group: 'UTXO Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
  {
    name: 'Median Value of Spent UTXOs',
    shortName: 'UTXO Value Spent (Median)',
    description: { default: `The median amount of coins${CURRENCY_PLACEHOLDER} in spent transaction outputs.` },
    path: 'blockchain/utxo_spent_value_median',
    categories: [CATEGORIES.unspentSpentOutputs, CATEGORIES.volume],
    group: 'UTXO Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,
  },
]);

export const miningMetrics = enhanceMetrics([
  {
    name: 'Mining Difficulty',
    shortName: 'Difficulty',
    description: {
      default:
        'The current estimated number of hashes required to mine a block. Note: %ASSET% difficulty is often denoted as the relative difficulty with respect to the genesis block, which required approximately 2^32 hashes. For better comparison across blockchains, our values are denoted in raw hashes.',
    },
    path: 'mining/difficulty_latest',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Network Security',

    pulse: {
      multiplier: TERA_MULTIPLIER,
      formatter: FORMATTER_TRILLION,
      maxDecimals: 0,
    },
  },
  {
    name: 'Mean Hash Rate',
    shortName: 'Hash Rate',
    description: {
      default: 'The average estimated number of hashes per second produced by the miners in the network.',
    },
    path: 'mining/hash_rate_mean',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Network Security',

    formatter: FORMATTER_UNIT_PER_SECOND,

    pulse: {
      multiplier: TERA_MULTIPLIER,
      formatter: FORMATTER_TH_PER_SECOND,
      maxDecimals: 0,
    },
  },
  {
    name: 'Total Miner Revenue',
    shortName: 'Miner Revenue (Total)',
    description: { default: `The total miner revenue${CURRENCY_PLACEHOLDER}, i.e. fees plus newly minted coins.` },
    path: 'mining/revenue_sum',
    categories: [CATEGORIES.miners],
    group: 'Miner Revenue',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.CURRENCY, params.MINER],
    minerOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Percent Miner Revenue from Fees',
    shortName: 'Miner Revenue (Fees)',
    description: {
      default: 'The percentage of miner revenue derived from fees, i.e. fees divided by fees plus minted coins.',
    },
    path: 'mining/revenue_from_fees',
    categories: [CATEGORIES.miners, CATEGORIES.fees],
    group: 'Miner Revenue',

    formatter: FORMATTER_PERCENT,

    tier: TIER_2,
  },
  {
    name: 'Total Miner Revenue from Block Rewards',
    shortName: 'Miner Revenue (Block Rewards)',
    description: {
      default: `The total amount${CURRENCY_PLACEHOLDER} of newly minted coins, i.e. block rewards.`,
    },
    path: 'mining/volume_mined_sum',
    categories: [CATEGORIES.miners],
    group: 'Miner Revenue',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.CURRENCY, params.MINER],
    minerOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Balance in Miner Wallets',
    shortName: 'Miner Balance',
    description: {
      default: `The total supply${CURRENCY_PLACEHOLDER} held in miner addresses.`,
    },
    path: 'distribution/balance_miners_sum',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.distribution],
    group: 'Entity Balances',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.CURRENCY, params.MINER],
    minerOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Total Transfer Volume to Miners',
    shortName: 'Miner Inflow Volume',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred to miner addresses.`,
    },
    path: 'transactions/transfers_volume_to_miners_sum',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.transactions, CATEGORIES.volume],
    group: 'Miner Activity',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.CURRENCY, params.MINER],
    minerOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Total Transfer Volume from Miners',
    shortName: 'Miner Outflow Volume',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} transferred from miner addresses.`,
    },
    path: 'transactions/transfers_volume_from_miners_sum',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.transactions, CATEGORIES.volume],
    group: 'Miner Activity',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.CURRENCY, params.MINER],
    minerOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Net Transfer Volume from/to Miners',
    shortName: 'Miner Netflow Volume',
    description: {
      default: `The difference between miner's inflow and outflow${CURRENCY_PLACEHOLDER}, i.e the net flow of coins into/out of miner addresses.`,
    },
    path: 'transactions/transfers_volume_miners_net',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.transactions, CATEGORIES.volume],
    group: 'Miner Activity',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.CURRENCY, params.MINER],
    minerOption: true,
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Miner Outflow Multiple',
    shortName: 'Miner Outflow Multiple',
    description: {
      default: `The Miner Outflow Multiple indicates periods where the amount of bitcoins flowing out of miner addresses is high with respect to its historical average. It is defined as the ratio of the miners' outflow and its 365 day MA.`,
    },
    path: 'mining/miners_outflow_multiple',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.transactions, CATEGORIES.ratios],
    group: 'Miner Activity',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.MINER],
    minerOption: true,

    tier: TIER_3,
  },
  {
    name: 'Miner Unspent Supply',
    shortName: 'Miner Unspent Supply',
    description: {
      default: `The total amount of coins${CURRENCY_PLACEHOLDER} in coinbase transactions that have never been moved.`,
    },
    path: 'mining/miners_unspent_supply',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Balance in Miner Wallets',
    shortName: 'Miner Balance (Stacked)',
    description: {
      default: `The total supply${CURRENCY_PLACEHOLDER} held in miner addresses.`,
    },
    path: 'distribution/balance_miners_all',

    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    scales: [SCALE_LIN],
    formatter: FORMATTER_CURRENCY,
    currencyOption: true,
    movingAverageOption: false,
    movingMedianOption: false,

    compare: false,

    tier: TIER_3,
  },
  {
    name: 'Number of Transfers from Miner Wallets',
    shortName: 'Miner Outgoing Transfers',
    description: {
      default: `The total number of transfers in which the sender is a miners' address.`,
    },
    path: 'transactions/transfers_from_miners_count',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.transactions],
    group: 'Miner Activity',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.MINER],
    minerOption: true,

    tier: TIER_3,
  },
  {
    name: 'Number of Transfers into Miner Wallets',
    shortName: 'Miner Incoming Transfers',
    description: {
      default: `The total number of transfers in which the receiver is a miners' address.`,
    },
    path: 'transactions/transfers_to_miners_count',
    categories: [CATEGORIES.entities, CATEGORIES.miners, CATEGORIES.transactions],
    group: 'Miner Activity',
    dependency: 'miners',

    requiredParams: [params.ASSET, params.MINER],
    minerOption: true,

    tier: TIER_3,
  },
  {
    name: 'Difficulty Ribbon',
    shortName: 'Difficulty Ribbon',
    description: {
      default: `The Difficulty Ribbon is an indicator that uses simple moving averages (200d, 128d, 90d, 60d, 40d, 25d, 14d) of the Bitcoin [mining difficulty](https://studio.glassnode.com/metrics?a=BTC&m=mining.DifficultyLatest) to create the ribbon. Historically, periods when the ribbon compresses have been good buying opportunities. The Difficulty Ribbon was created by [Willy Woo](https://twitter.com/woonomic). For more information read this [article](https://woobull.com/introducing-the-difficulty-ribbon-the-best-times-to-buy-bitcoin/).`,
    },
    path: 'indicators/difficulty_ribbon',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.miners],
    group: 'Ribbons',

    compare: false,
    movingAverageOption: false,
    movingMedianOption: false,
    defaultScale: SCALE_LOG,
    logTickInterval: 1,
    resolutionOption: false,

    tier: TIER_1,
  },

  {
    name: 'Difficulty Ribbon Compression',
    shortName: 'Difficulty Ribbon Compression',
    description: {
      default: `[Difficulty Ribbon Compression](https://twitter.com/n3ocortex/status/1290670685933666305) is a market indicator that uses a normalized standard deviation to quantify compression of the [Difficulty Ribbon](https://studio.glassnode.com/metricsmetrics?a=BTC&m=indicators.DifficultyRibbon). Historically, zones of high compression (low values) have been good buying opportunities. The compression threshold is set here at 0.05.`,
    },
    path: 'indicators/difficulty_ribbon_compression',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.miners],
    group: 'Ribbons',

    resolutionOption: false,

    tier: TIER_2,
  },
  {
    name: 'Hash Ribbon',
    shortName: 'Hash Ribbon',
    description: {
      default: `The Hash Ribbon is a market indicator that assumes that Bitcoin tends to reach a bottom when miners capitulate, i.e. when Bitcoin becomes too expensive to mine relative to the cost of mining. The Hash Ribbon indicates that the worst of the miner capitulation is over when the 30d MA of the hash rate crosses above the 60d MA – and have therefore shown to be good buying opportunities. The Hash Ribbon was created by [Charles Edwards](https://twitter.com/caprioleio). For more information read his [introductory article](https://medium.com/capriole/hash-ribbons-bitcoin-bottoms-60da13095836).`,
    },
    path: 'indicators/hash_ribbon',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.miners],
    group: 'Ribbons',

    compare: false,
    movingAverageOption: false,
    movingMedianOption: false,
    defaultScale: SCALE_LOG,
    logTickInterval: 1,

    tier: TIER_2,
  },
  {
    name: 'Aggregate Security Spend (Thermocap)',
    shortName: 'Thermocap',
    description: {
      default:
        'Aggregate security spend, or "Thermocap", is the aggregated amount of coins paid to miners and serves as a proxy to mining resources spent. It serves as a measure of the true capital flow into the network and is computed as the aggregate coinbase transactions multiplied by the price in USD at the time they were mined. This metric was first put forth by [Nic Carter](https://twitter.com/nic__carter).',
    },
    path: 'mining/thermocap',
    categories: [CATEGORIES.miners, CATEGORIES.fees],
    group: 'Network Security',

    formatter: FORMATTER_CURRENCY_USD,
    defaultScale: SCALE_LOG,

    tier: TIER_2,
  },
  {
    name: 'Market Cap to Thermocap Ratio',
    shortName: 'Market Cap to Thermocap Ratio',
    description: {
      default:
        "The Market Cap to Thermocap Ratio is simply defined as Market cap / Thermocap, and can be used to assess if the asset's price is currently trading at a premium with respect to total security spend by miners. The ratio is adjusted to account for the increasing circulating supply over time.",
    },
    path: 'mining/marketcap_thermocap_ratio',
    categories: [CATEGORIES.miners, CATEGORIES.fees, CATEGORIES.ratios],
    group: 'Network Security',

    tier: TIER_2,
  },
]);

export const blocksMetrics = enhanceMetrics([
  {
    name: 'Block Height',
    shortName: 'Block Height',
    description: {
      default: 'The block height, i.e. the total number of blocks ever created and included in the main blockchain.',
    },
    path: 'blockchain/block_height',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Blocks',
  },
  {
    name: 'Number of Blocks Mined',
    shortName: 'Blocks Mined',
    description: { default: 'The number of blocks created and included in the main blockchain in that time period.' },
    path: 'blockchain/block_count',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Blocks',
  },
  {
    name: 'Mean Block Interval',
    shortName: 'Block Interval (Mean)',
    description: { default: 'The mean time (in seconds) between mined blocks.' },
    path: 'blockchain/block_interval_mean',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Blocks',

    formatter: FORMATTER_SECOND,
  },
  {
    name: 'Median Block Interval',
    shortName: 'Block Interval (Median)',
    description: { default: 'The median time (in seconds) between mined blocks.' },
    path: 'blockchain/block_interval_median',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Blocks',

    formatter: FORMATTER_SECOND,
  },
  {
    name: 'Total Block Size',
    shortName: 'Block Size (Total)',
    description: { default: 'The total size of all blocks created within the time period (in bytes).' },
    path: 'blockchain/block_size_sum',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Blocks',
  },
  {
    name: 'Mean Block Size',
    shortName: 'Block Size (Mean)',
    description: { default: 'The mean size of all blocks created within the time period (in bytes).' },
    path: 'blockchain/block_size_mean',
    categories: [CATEGORIES.miners, CATEGORIES.networkStats],
    group: 'Blocks',
  },
]);

export const indicatorsMetrics = enhanceMetrics([
  {
    name: 'Spent Output Profit Ratio (SOPR)',
    shortName: 'SOPR',
    description: {
      default:
        'The Spent Output Profit Ratio (SOPR) is computed by dividing the realized value (in USD) divided by the value at creation (USD) of a spent output. Or simply: price sold / price paid. This metric was created by [Renato Shirakashi](https://twitter.com/renato_shira). For a detailed commentary see [this post](https://medium.com/unconfiscatable/introducing-sopr-spent-outputs-to-predict-bitcoin-lows-and-tops-ceb4536b3b9)',
    },
    path: 'indicators/sopr',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'SOPR',

    scales: [SCALE_LIN],
    start: {
      BTC: [1356912000, 1356912000],
    },
  },
  {
    name: 'Adjusted SOPR (aSOPR)',
    shortName: 'aSOPR',
    description: { default: 'Adjusted SOPR is SOPR ignoring all outputs with a lifespan of less than 1 hour.' },
    path: 'indicators/sopr_adjusted',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'SOPR',

    scales: [SCALE_LIN],
    start: {
      BTC: [1356912000, 1356912000],
    },

    tier: TIER_2,
  },
  {
    name: 'Long Term Holder SOPR',
    shortName: 'LTH-SOPR',
    description: {
      default:
        'Long Term Holder SOPR (LTH-SOPR) is SOPR that takes into account only spent outputs with a lifespan of at least 155 days and serves as an indicator to assess the behaviour of long term investors. For more information see [this article](https://insights.glassnode.com/sth-lth-sopr-mvrv).',
    },
    path: 'indicators/sopr_more_155',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios, CATEGORIES.longShortTermHolders],
    group: 'SOPR',

    scales: [SCALE_LOG],
    start: {
      BTC: [1356912000, 1356912000],
    },

    tier: TIER_3,
  },
  {
    name: 'Short Term Holder SOPR',
    shortName: 'STH-SOPR',
    description: {
      default:
        'Short Term Holder SOPR (STH-SOPR) is SOPR that takes into account only spent outputs younger than 155 days and serves as an indicator to assess the behaviour of short term investors. For more information see [this article](https://insights.glassnode.com/sth-lth-sopr-mvrv).',
    },
    path: 'indicators/sopr_less_155',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios, CATEGORIES.longShortTermHolders],
    group: 'SOPR',

    scales: [SCALE_LIN],
    start: {
      BTC: [1356912000, 1356912000],
    },

    tier: TIER_3,
  },
  {
    name: 'Net Unrealized Profit/Loss (NUPL)',
    shortName: 'Net Unrealized Profit/Loss (NUPL)',
    description: {
      default:
        'Net Unrealized Profit/Loss is the difference between Relative Unrealized Profit and Relative Unrealized Loss. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d). This metric can also be calculated by subtracting realised cap from market cap, and dividing the result by the market cap as described in [this article](https://medium.com/@adamant_capital/a-primer-on-bitcoin-investor-sentiment-and-changes-in-saving-behavior-a5fb70109d32).',
    },
    path: 'indicators/net_unrealized_profit_loss',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss],
    group: 'Unrealized Profit/Loss',

    scales: [SCALE_LIN],

    tier: TIER_2,
  },
  {
    name: 'Relative Unrealized Profit',
    shortName: 'Relative Unrealized Profit',
    description: {
      default:
        'Relative Unrealized Profit is defined as the total profit in USD of all coins in existence whose price at realisation time was lower than the current price normalised by the market cap. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).',
    },
    path: 'indicators/unrealized_profit',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss],
    group: 'Unrealized Profit/Loss',

    tier: TIER_2,
  },
  {
    name: 'Relative Unrealized Loss',
    shortName: 'Relative Unrealized Loss',
    description: {
      default:
        'Relative Unrealized Loss is defined as the total loss in USD of all coins in existence whose price at realisation time was higher than the current price normalised by the market cap. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).',
    },
    path: 'indicators/unrealized_loss',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss],
    group: 'Unrealized Profit/Loss',

    tier: TIER_2,
  },
  {
    name: 'Long Term Holder NUPL',
    shortName: 'LTH-NUPL',
    description: {
      default:
        'Long Term Holder NUPL (LTH-NUPL) is Net Unrealized Profit/Loss that takes into account only UTXOs with a lifespan of at least 155 days and serves as an indicator to assess the behaviour of long term investors. For more information see [this article](https://insights.glassnode.com/sth-lth-sopr-mvrv).',
    },
    path: 'indicators/nupl_more_155',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders],
    group: 'Unrealized Profit/Loss',

    scales: [SCALE_LIN],

    tier: TIER_3,
  },
  {
    name: 'Short Term Holder NUPL',
    shortName: 'STH-NUPL',
    description: {
      default:
        'Short Term Holder NUPL (STH-NUPL) is Net Unrealized Profit/Loss that takes into account only UTXOs younger than 155 days and serves as an indicator to assess the behaviour of short term investors. For more information see [this article](https://insights.glassnode.com/sth-lth-sopr-mvrv).',
    },
    path: 'indicators/nupl_less_155',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders],
    group: 'Unrealized Profit/Loss',

    scales: [SCALE_LIN],

    tier: TIER_3,
  },
  {
    name: 'Realized Profit',
    shortName: 'Realized Profit',
    description: {
      default:
        'Realized Profit denotes the total profit (USD value) of all moved coins whose price at their last movement was lower than the price at the current movement.',
    },
    path: 'indicators/realized_profit',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss, CATEGORIES.volume],
    group: 'Realized Profit/Loss',

    formatter: FORMATTER_CURRENCY_USD,

    tier: TIER_2,
  },
  {
    name: 'Realized Loss',
    shortName: 'Realized Loss',
    description: {
      default:
        'Realized Loss denotes the total loss (USD value) of all moved coins whose price at their last movement was higher than the price at the current movement.',
    },
    path: 'indicators/realized_loss',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss, CATEGORIES.volume],
    group: 'Realized Profit/Loss',

    formatter: FORMATTER_CURRENCY_USD,

    tier: TIER_2,
  },
  {
    name: 'Net Realized Profit/Loss',
    shortName: 'Net Realized Profit/Loss',
    description: {
      default:
        'Net Realized Profit/Loss is the net profit or loss of all moved coins, and is defined by the difference of [Realized Profit](https://studio.glassnode.com/metrics?a=BTC&m=indicators.RealizedProfit) - [Realized Loss](https://studio.glassnode.com/metrics?a=BTC&m=indicators.RealizedLoss).',
    },
    path: 'indicators/net_realized_profit_loss',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss, CATEGORIES.volume],
    group: 'Realized Profit/Loss',

    formatter: FORMATTER_CURRENCY_USD,
    scales: [SCALE_LIN],

    tier: TIER_2,
  },
  {
    name: 'Puell Multiple',
    shortName: 'Puell Multiple',
    description: {
      default:
        'The Puell Multiple is calculated by dividing the daily issuance value of bitcoins (in USD) by the 365-day moving average of daily issuance value. This metric was created by [David Puell](https://twitter.com/kenoshaking). For a detailed description see [this article](https://medium.com/unconfiscatable/the-puell-multiple-bed755cfe358) by [@cryptopoiesis](https://twitter.com/cryptopoiesis).',
    },
    path: 'indicators/puell_multiple',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.miners, CATEGORIES.ratios],
    group: 'Coin Issuance',

    scales: [SCALE_LOG],
    resolutionOption: false,

    tier: TIER_2,
  },
  {
    name: 'Coin Days Destroyed (CDD)',
    shortName: 'Coin Days Destroyed (CDD)',
    description: {
      default:
        'Coin Days Destroyed (CDD) for any given transaction is calculated by taking the number of coins in a transaction and multiplying it by the number of days it has been since those coins were last spent.',
    },
    path: 'indicators/cdd',
    categories: [CATEGORIES.lifespan],
    group: 'Coin Days Destroyed',

    tier: TIER_2,
  },
  {
    name: 'Supply-Adjusted CDD',
    shortName: 'Supply-Adjusted CDD',
    description: {
      default:
        'Adjusted Coin Days Destroyed simply divides CDD by the circulating supply (total amount of coins issued). Adjusted CDD more accurately represents the quantity of native coins sold by long-term holders over time.',
    },
    path: 'indicators/cdd_supply_adjusted',
    categories: [CATEGORIES.lifespan],
    group: 'Coin Days Destroyed',

    tier: TIER_2,
  },
  {
    name: 'Binary CDD',
    shortName: 'Binary CDD',
    description: {
      default:
        'Binary Coin Days Destroyed (Binary CDD) is computed by thresholding Adjusted CDD by its average over time, i.e. taking the mean destruction over time and asking whether there were more Adjusted CDDs destroyed today than on average. Binary CDD is used in order to minimize the impact of exchange movements, which do not accurately reflect behavior of the long-term holders. This metric was developed by [Hans Hauge](https://twitter.com/hansthered) and [Ikigai](https://twitter.com/Ikigai_fund). For more information see [this post](https://www.kanaandkatana.com/valuation-depot-contents/2019/5/30/exploration-of-bitcoin-days-destroyed).',
    },
    path: 'indicators/cdd_supply_adjusted_binary',
    categories: [CATEGORIES.lifespan],
    group: 'Coin Days Destroyed',

    scales: [SCALE_LIN],

    compare: false,

    tier: TIER_2,
  },
  {
    name: 'Reserve Risk',
    shortName: 'Reserve Risk',
    description: {
      default:
        'Reserve Risk is defined as price / HODL Bank. It is used to assess the confidence of long-term holders relative to the price of the native coin at any given point in time. When confidence is high and price is low, there is an attractive risk/reward to invest (Reserve Risk is low). When confidence is low and price is high then risk/reward is unattractive at that time (Reserve Risk is high).\n\nThis metric was created by [@hansthered](https://twitter.com/hansthered). For more information see [this article](https://www.kanaandkatana.com/valuation-depot-contents/2019/5/30/exploration-of-bitcoin-days-destroyed).',
    },
    path: 'indicators/reserve_risk',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.lifespan],
    group: 'Coin Days Destroyed',

    scales: [SCALE_LOG],

    pulse: {
      maxDecimals: 6,
    },

    tier: TIER_2,
  },
  {
    name: 'Liveliness',
    shortName: 'Liveliness',
    description: {
      default:
        'Liveliness is defined as the ratio of the sum of Coin Days Destroyed and the sum of all coin days ever created. Liveliness increases as long term holder liquidate positions and decreases while they accumulate to HODL. This metric was created by [Tamas Blummer](https://twitter.com/TamasBlummer). For a detailed commentary see [this post](https://medium.com/@tamas.blummer/liveliness-of-bitcoin-174001d016da).',
    },
    path: 'indicators/liveliness',
    categories: [CATEGORIES.lifespan, CATEGORIES.ratios],
    group: 'Liveliness',

    tier: TIER_2,
  },
  {
    name: 'Average Coin Dormancy',
    shortName: 'Dormancy',
    description: {
      default:
        'Dormancy is the average number of days destroyed per coin transacted, and is defined as the ratio of coin days destroyed and total transfer volume. This metric was created by [Reginald Smith](https://twitter.com/SupremeVinegar) and [David Puell](https://twitter.com/kenoshaking). Read more in [this article](https://medium.com/adaptivecapital/bitcoin-average-dormancy-28f88ea4c2ce).',
    },
    path: 'indicators/average_dormancy',
    categories: [CATEGORIES.lifespan],
    group: 'Dormancy',

    tier: TIER_2,
  },
  {
    name: 'Supply-Adjusted Dormancy',
    shortName: 'Supply-Adjusted Dormancy',
    description: {
      default:
        'Dormancy is the average number of days destroyed per coin transacted, and is defined as the ratio of coin days destroyed and total transfer volume. This metric was created by [Reginald Smith](https://twitter.com/SupremeVinegar) and [David Puell](https://twitter.com/kenoshaking). Read more in [this article](https://medium.com/adaptivecapital/bitcoin-average-dormancy-28f88ea4c2ce).',
    },
    path: 'indicators/average_dormancy_supply_adjusted',
    categories: [CATEGORIES.lifespan],
    group: 'Dormancy',

    tier: TIER_2,
  },
  {
    name: 'Stablecoin Supply Ratio (SSR)',
    shortName: 'Stablecoin Supply Ratio (SSR)',
    description: {
      default:
        'The Stablecoin Supply Ratio (SSR) is the ratio between Bitcoin supply and the supply of stablecoins denoted in BTC, or: Bitcoin Market cap / Stablecoin Market cap. We use the following stablecoins for the supply: USDT, TUSD, USDC, PAX, GUSD, DAI, SAI, and BUSD. When the SSR is low, the current stablecoin supply has more "buying power" to purchase BTC. It serves as a proxy for the supply/demand mechanics between BTC and USD. For more information see [this article](https://medium.com/@glassnode/stablecoins-buying-power-over-bitcoin-3475c0d8779d).',
    },
    path: 'indicators/ssr',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'Stablecoin',

    tier: TIER_2,
  },
  {
    name: 'Average Spent Output Lifespan (ASOL)',
    shortName: 'ASOL',
    description: {
      default:
        'Average Spent Output Lifespan (ASOL) is the average age (in days) of spent transaction outputs. Outputs with a lifespan of less than 1h are discarded.',
    },
    path: 'indicators/asol',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    tier: TIER_2,
  },
  {
    name: 'Median Spent Output Lifespan (MSOL)',
    shortName: 'MSOL',
    description: {
      default:
        'Median Spent Output Lifespan (MSOL) is the median age (in days) of spent transaction outputs. Outputs with a lifespan of less than 1h are discarded.',
    },
    path: 'indicators/msol',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    tier: TIER_2,
  },
  {
    name: 'Spent Output Age Bands',
    shortName: 'Spent Output Age Bands',
    description: {
      default:
        'Spent Output Age Bands is a bundle of all spent outputs that were created within a specified age band. Each line represents the percentage of spent outputs that were created within the time period denoted in the legend.',
    },
    path: 'indicators/soab',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    resolutionOption: false,
    formatter: FORMATTER_PERCENT,
    scales: [SCALE_LIN],
    start: {
      BTC: [1279411200, 1279411200],
    },

    compare: false,

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan < 1h',
    shortName: 'Spent Outputs < 1h',
    description: { default: 'The total number of spent outputs that were created within the last hour.' },
    path: 'indicators/sol_1h',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 1h-24h',
    shortName: 'Spent Outputs 1h-24h',
    description: { default: 'The total number of spent outputs that were created between 1 hour and 24 hours ago.' },
    path: 'indicators/sol_1h_24h',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 1d-1w',
    shortName: 'Spent Outputs 1d-1w',
    description: { default: 'The total number of spent outputs that were created between 1 day and 1 week ago.' },
    path: 'indicators/sol_1d_1w',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 1w-1m',
    shortName: 'Spent Outputs 1w-1m',
    description: { default: 'The total number of spent outputs that were created between 1 week and 1 month ago.' },
    path: 'indicators/sol_1w_1m',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 1m-3m',
    shortName: 'Spent Outputs 1m-3m',
    description: { default: 'The total number of spent outputs that were created between 1 month and 3 months ago.' },
    path: 'indicators/sol_1m_3m',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 3m-6m',
    shortName: 'Spent Outputs 3m-6m',
    description: { default: 'The total number of spent outputs that were created between 3 months and 6 months ago.' },
    path: 'indicators/sol_3m_6m',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 6m-12m',
    shortName: 'Spent Outputs 6m-12m',
    description: { default: 'The total number of spent outputs that were created between 6 months and 12 months ago.' },
    path: 'indicators/sol_6m_12m',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 1y-2y',
    shortName: 'Spent Outputs 1y-2y',
    description: { default: 'The total number of spent outputs that were created between 1 year and 2 years ago.' },
    path: 'indicators/sol_1y_2y',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 2y-3y',
    shortName: 'Spent Outputs 2y-3y',
    description: { default: 'The total number of spent outputs that were created between 2 years and 3 years ago.' },
    path: 'indicators/sol_2y_3y',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 3y-5y',
    shortName: 'Spent Outputs 3y-5y',
    description: { default: 'The total number of spent outputs that were created between 3 years and 5 years ago.' },
    path: 'indicators/sol_3y_5y',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 5y-7y',
    shortName: 'Spent Outputs 5y-7y',
    description: { default: 'The total number of spent outputs that were created between 5 years and 7 years ago.' },
    path: 'indicators/sol_5y_7y',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan 7y-10y',
    shortName: 'Spent Outputs 7y-10y',
    description: { default: 'The total number of spent outputs that were created between 7 years and 10 years ago.' },
    path: 'indicators/sol_7y_10y',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Spent Output Lifespan > 10y',
    shortName: 'Spent Outputs > 10y',
    description: { default: 'The total number of spent outputs that were created more than 10 years ago.' },
    path: 'indicators/sol_more_10y',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    start: {
      BTC: [1279411200, 1279411200],
    },

    tier: TIER_2,
  },
  {
    name: 'Network Value to Transactions Ratio (NVT)',
    shortName: 'NVT Ratio',
    description: {
      default:
        'The Network Value to Transactions (NVT) Ratio is computed by dividing the market cap by the transferred on-chain volume measured in USD. The NVT Ratio was created by [Willy Woo](https://twitter.com/woonomic).',
    },
    path: 'indicators/nvt',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'NVT',

    tier: TIER_2,
  },
  {
    name: 'Network Value to Transactions Signal',
    shortName: 'NVT Signal',
    description: {
      default:
        'The NVT Signal (NVTS) is a modified version of the original NVT Ratio. It uses a 90 day moving average of the daily transaction volume in the denominator instead of the raw daily transaction volume. This moving average improves the ratio to better function as a leading indicator.',
    },
    path: 'indicators/nvts',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'NVT',

    tier: TIER_2,
  },
  {
    name: 'Velocity',
    shortName: 'Velocity',
    description: {
      default:
        'Velocity is a measure of how quickly units are circulating in the network and is calculated by dividing the on-chain transaction volume (in USD) by the market cap, i.e. the inverse of the NVT ratio.',
    },
    path: 'indicators/velocity',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'NVT',

    tier: TIER_2,
  },
  {
    name: 'Hodled or Lost Coins',
    shortName: 'Hodled or Lost Coins',
    description: {
      default:
        'Lost or HODLed Bitcoins indicates moves of large and old stashes. It is calculated by substracting [Liveliness](https://studio.glassnode.com/metrics?a=BTC&m=indicators.Liveliness) from 1 and multiplying the result by the circulating supply. This metric was first coined by [Adamant Capital](https://twitter.com/Adamant_Capital). For more information see [this article](https://medium.com/@adamant_capital/a-primer-on-bitcoin-investor-sentiment-and-changes-in-saving-behavior-a5fb70109d32).',
    },
    path: 'indicators/hodled_lost_coins',
    categories: [CATEGORIES.distribution, CATEGORIES.supply, CATEGORIES.longShortTermHolders],
    group: 'Hodlers',

    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Hodler Net Position Change',
    shortName: 'Hodler Net Position Change',
    description: {
      default:
        'Hodler Net Position Change shows the monthly position change of long term investors (HODLers). It indicates when HODLers cash out (negative) and when net new positions are accumulated by HODLers. This metric was first coined by [Adamant Capital](https://twitter.com/Adamant_Capital). For more information see [this article](https://medium.com/@adamant_capital/a-primer-on-bitcoin-investor-sentiment-and-changes-in-saving-behavior-a5fb70109d32).',
    },
    path: 'indicators/hodler_net_position_change',
    categories: [CATEGORIES.distribution, CATEGORIES.supply, CATEGORIES.longShortTermHolders],
    group: 'Hodlers',

    formatter: FORMATTER_CURRENCY,

    compare: false,
    scales: [SCALE_LIN],

    tier: TIER_3,
  },
  {
    name: 'Stock-to-Flow Ratio',
    shortName: 'Stock-to-Flow Ratio',
    description: {
      default:
        "The Stock to Flow (S/F) Ratio is a popular model that assumes that scarcity drives value. Stock to Flow is defined as the ratio of the current stock of a commodity (i.e. circulating Bitcoin supply) and the flow of new production (i.e. newly mined bitcoins). Bitcoin's price has historically followed the S/F Ratio and therefore it is a model that can be used to predict future Bitcoin valuations. This metric was first coined by [PlanB](https://twitter.com/100trillionUSD). For a detailed description see this [article](https://medium.com/@100trillionUSD/modeling-bitcoins-value-with-scarcity-91fa0fc03e25).",
    },
    path: 'indicators/stock_to_flow_ratio',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'Stock-to-Flow',

    scales: [SCALE_LOG],
    sameAxis: true,
    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,
    scaleOption: false,
    logTickInterval: 1,
    formatter: FORMATTER_CURRENCY_USD,

    compare: false,

    tier: TIER_1,
    upgradeOption: false,
  },
  {
    name: 'Stock-to-Flow Deflection',
    shortName: 'Stock-to-Flow Deflection',
    description: {
      default:
        'The Stock to Flow (S/F) Deflection is the ratio between the current Bitcoin price and the [S/F model](https://studio.glassnode.com/metrics?a=BTC&m=indicators.StockToFlowRatio). If deflection is ≥ 1 it means that Bitcoin is overvalued according to the S/F model, otherwise undervalued.',
    },
    path: 'indicators/stock_to_flow_deflection',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'Stock-to-Flow',

    scales: [SCALE_LOG],

    tier: TIER_2,
  },
  {
    name: 'UTXO Realized Price Distribution (URPD)',
    shortName: 'URPD (ATH-Partitioned)',
    description: {
      default:
        'UTXO Realized Price Distribution (URPD) shows at which prices the current set of Bitcoin UTXOs were created, i.e. each bar shows the amount of existing bitcoins that last moved within that specified price bucket. The price specified on the x-axis refers to the lower bound of that bucket. ATH-partitioned means that the price buckets are defined by dividing the range between 0 and the current ATH in 100 equally-spaced partitions.',
    },
    path: 'indicators/utxo_realized_price_distribution_ath',
    categories: [CATEGORIES.distribution, CATEGORIES.unspentSpentOutputs],
    group: 'Price Distribution',

    chartType: CHART_TYPE_COLUMN,
    formatter: FORMATTER_CURRENCY,

    compare: false,
    price: false,
    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,

    tier: TIER_3,
  },
  {
    name: 'UTXO Realized Price Distribution (URPD)',
    shortName: 'URPD (Percent-Partitioned)',
    description: {
      default:
        "UTXO Realized Price Distribution (URPD) shows at which prices the current set of Bitcoin UTXOs were created, i.e. each bar shows the amount of existing bitcoins that last moved within that specified price bucket. The price specified on the x-axis refers to the lower bound of that bucket. %-partitioned means that the price buckets are defined by taking the day's closing price and creating 50 equally-spaced bucket each above and below the closing price in steps of +/- 2%.",
    },
    path: 'indicators/utxo_realized_price_distribution_percent',
    categories: [CATEGORIES.distribution, CATEGORIES.unspentSpentOutputs],
    group: 'Price Distribution',

    chartType: CHART_TYPE_COLUMN,
    formatter: FORMATTER_CURRENCY,

    compare: false,
    price: false,
    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,

    tier: TIER_3,
  },
  {
    name: 'Spent Output Price Distribution (SOPD)',
    shortName: 'SOPD (ATH-Partitioned)',
    description: {
      default: `Spent Output Price Distribution (SOPD) shows at which prices UTXOs were spent that day, i.e. each bar shows the amount of ${ASSET_PLACEHOLDER} volume that was moved within that specified price bucket. The price specified on the x-axis refers to the lower bound of that bucket. ATH-partitioned means that the price buckets are defined by dividing the range between 0 and the current ATH in 100 equally-spaced partitions.`,
    },
    path: 'indicators/spent_output_price_distribution_ath',
    categories: [CATEGORIES.distribution, CATEGORIES.unspentSpentOutputs],
    group: 'Price Distribution',

    chartType: CHART_TYPE_COLUMN,
    formatter: FORMATTER_CURRENCY,

    compare: false,
    price: false,
    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,

    tier: TIER_3,
  },
  {
    name: 'Spent Output Price Distribution (SOPD)',
    shortName: 'SOPD (Percent-Partitioned)',
    description: {
      default: `Spent Output Price Distribution (SOPD) shows at which prices UTXOs were spent that day, i.e. each bar shows the amount of ${ASSET_PLACEHOLDER} volume that was moved within that specified price bucket. The price specified on the x-axis refers to the lower bound of that bucket. %-partitioned means that the price buckets are defined by taking the day's closing price and creating 50 equally-spaced bucket each above and below the current price in steps of +/- 2%.`,
    },
    path: 'indicators/spent_output_price_distribution_percent',
    categories: [CATEGORIES.distribution, CATEGORIES.unspentSpentOutputs],
    group: 'Price Distribution',

    chartType: CHART_TYPE_COLUMN,
    formatter: FORMATTER_CURRENCY,

    compare: false,
    price: false,
    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,

    tier: TIER_3,
  },
]);

if (WITH_INTERNAL_METRICS) {
  indicatorsMetrics.push(
    ...enhanceMetrics([
      {
        name: '🙈Cumulative Value-Days Destroyed (CVDD)',
        shortName: '🙈CVDD',
        description: {
          default:
            'Cumulative Value-Days Destroyed (CVDD) is the ratio of the cumulative USD value of [Coin Days Destroyed](https://studio.glassnode.com/metrics?a=BTC&m=indicators.Cdd) and the market age (in days). Historically, CVDD has been an accurate indicator for global Bitcoin market bottoms. This metric was created by [Willy Woo](https://twitter.com/woonomic). For more information see [this article](https://woobull.com/experiments-on-cumulative-destruction/).',
        },
        path: 'indicators/cvdd',
        categories: [CATEGORIES.marketIndicators, CATEGORIES.lifespan, CATEGORIES.internal],
        group: 'Coin Days Destroyed',

        resolutionOption: false,
        logTickInterval: 1,
        defaultScale: SCALE_LOG,
        sameAxis: true,

        tier: TIER_2,
        isNew: true,
      },
    ]),
  );
}

export const accountBasedMetrics = enhanceMetrics([
  {
    name: 'Entity-Adjusted SOPR',
    shortName: 'Entity-Adjusted SOPR',
    description: {
      default:
        'Entity-adjusted SOPR is an improved variant of [SOPR](https://studio.glassnode.com/metrics?a=BTC&m=indicators.Sopr) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted SOPR therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/sopr_account_based',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.ratios],
    group: 'SOPR',

    scales: [SCALE_LIN],
    start: {
      BTC: [1356912000, 1356912000],
    },

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted CDD',
    shortName: 'Entity-Adjusted CDD',
    description: {
      default:
        'Entity-adjusted CDD is an improved variant of [CDD](https://studio.glassnode.com/metrics?a=BTC&m=indicators.Cdd) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted CDD therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/cdd_account_based',
    categories: [CATEGORIES.lifespan],
    group: 'Coin Days Destroyed',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted ASOL',
    shortName: 'Entity-Adjusted ASOL',
    description: {
      default:
        'Entity-adjusted ASOL is an improved variant of [ASOL](https://studio.glassnode.com/metrics?a=BTC&m=indicators.Asol) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted ASOL therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/asol_account_based',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted MSOL',
    shortName: 'Entity-Adjusted MSOL',
    description: {
      default:
        'Entity-adjusted MSOL is an improved variant of [MSOL](https://studio.glassnode.com/metrics?a=BTC&m=indicators.Msol) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted MSOL therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/msol_account_based',
    categories: [CATEGORIES.lifespan, CATEGORIES.unspentSpentOutputs],
    group: 'Spent Output Lifespan',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted Liveliness',
    shortName: 'Entity-Adjusted Liveliness',
    description: {
      default:
        'Entity-adjusted Liveliness is an improved variant of [Liveliness](https://studio.glassnode.com/metrics?a=BTC&m=indicators.Liveliness) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted Liveliness therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/liveliness_account_based',
    categories: [CATEGORIES.lifespan, CATEGORIES.ratios],
    group: 'Liveliness',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted Dormancy',
    shortName: 'Entity-Adjusted Dormancy',
    description: {
      default:
        'Entity-adjusted Dormancy is an improved variant of [Average Coin Dormancy](https://studio.glassnode.com/metrics?a=BTC&m=indicators.AverageDormancy) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted Dormancy therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/dormancy_account_based',
    categories: [CATEGORIES.lifespan],
    group: 'Dormancy',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted Realized Cap',
    shortName: 'Entity-Adjusted Realized Cap',
    description: {
      default:
        'Entity-adjusted Realized Cap is an improved variant of [Realized Cap](https://studio.glassnode.com/metrics?a=BTC&m=market.PriceRealizedUsd) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted Realized Cap therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/rcap_account_based',
    categories: [CATEGORIES.priceAndMarketCap],
    group: 'Market Cap',

    formatter: FORMATTER_CURRENCY_USD,

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted MVRV',
    shortName: 'Entity-Adjusted MVRV',
    description: {
      default:
        'Entity-adjusted MVRV is an improved variant of [MVRV Ratio](https://studio.glassnode.com/metrics?a=BTC&m=market.Mvrv) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted MVRV therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/mvrv_account_based',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.priceAndMarketCap, CATEGORIES.ratios],
    group: 'MVRV',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted Relative Unrealized Profit',
    shortName: 'Entity-Adjusted Unrealized Profit',
    description: {
      default:
        'Entity-adjusted Relative Unrealized Profit is an improved variant of [Unrealized Profit](https://studio.glassnode.com/metrics?a=BTC&m=indicators.UnrealizedProfit) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted Relative Unrealized Proft therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/unrealized_profit_account_based',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss],
    group: 'Unrealized Profit/Loss',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted Relative Unrealized Loss',
    shortName: 'Entity-Adjusted Unrealized Loss',
    description: {
      default:
        'Entity-adjusted Relative Unrealized Loss is an improved variant of [Unrealized Loss](https://studio.glassnode.com/metrics?a=BTC&m=indicators.UnrealizedLoss) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted Relative Unrealized Loss therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/unrealized_loss_account_based',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss],
    group: 'Unrealized Profit/Loss',

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted NUPL',
    shortName: 'Entity-Adjusted NUPL',
    description: {
      default:
        'Entity-adjusted NUPL is an improved variant of [Net Unrealized Profit/Loss (NUPL)](https://studio.glassnode.com/metrics?a=BTC&m=indicators.NetUnrealizedProfitLoss) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted NUPL therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/net_unrealized_profit_loss_account_based',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss],
    group: 'Unrealized Profit/Loss',

    scales: [SCALE_LIN],

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted LTH-NUPL',
    shortName: 'Entity-Adjusted LTH-NUPL',
    description: {
      default:
        'Entity-adjusted LTH-NUPL is an improved variant of [Long-Term Holders Net Unrealized Profit/Loss (LTH-NUPL)](https://studio.glassnode.com/metrics?a=BTC&m=indicators.NuplMore155) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted LTH NUPL therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/nupl_more_155_account_based',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders],
    group: 'Unrealized Profit/Loss',

    scales: [SCALE_LIN],

    tier: TIER_3,
  },
  {
    name: 'Entity-Adjusted STH-NUPL',
    shortName: 'Entity-Adjusted STH-NUPL',
    description: {
      default:
        'Entity-adjusted STH-NUPL is an improved variant of [Shot-Term Holders Net Unrealized Profit/Loss (STH-NUPL)](https://studio.glassnode.com/metrics?a=BTC&m=indicators.NuplLess155) that discards transactions between addresses of the same entity ("in-house" transactions). Entity-adjusted STH NUPL therefore accounts for real economic activity only, and provides an improved market signal compared to its raw UTXO-based counterpart. For detailed information read this article [article](http://insights.glassnode.com/account-based-metrics).',
    },
    path: 'indicators/nupl_less_155_account_based',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders],
    group: 'Unrealized Profit/Loss',

    scales: [SCALE_LIN],

    tier: TIER_3,
  },
]);

export const priceMetrics = enhanceMetrics([
  {
    name: 'Price (USD)',
    shortName: 'Price',
    description: { default: "The asset's price in USD." },
    path: 'market/price_usd_close',
    categories: [CATEGORIES.priceAndMarketCap],
    group: 'Price',

    formatter: FORMATTER_CURRENCY_USD,

    upgradeOption: false,
  },
]);

export const marketMetrics = enhanceMetrics([
  {
    name: 'Price',
    shortName: 'Price',
    description: {
      default: "OHLC candlestick chart of the asset's price in USD.",
    },
    path: 'market/price_usd_ohlc',
    categories: [CATEGORIES.priceAndMarketCap],
    group: 'Price',

    compare: false,
    price: false,
    movingAverageOption: false,
    movingMedianOption: false,
    defaultResolution: RESOLUTION_1H,
    resolutionRestricted: false,
    resolutionOption: false,
    sameAxis: true,

    upgradeOption: false,
  },
  {
    name: 'Price Drawdown from ATH',
    shortName: 'Price Drawdown from ATH',
    description: {
      default: "The percent drawdown of the asset's price from the previous all-time high.",
    },
    path: 'market/price_drawdown_relative',
    categories: [CATEGORIES.priceAndMarketCap],
    group: 'Price',

    scales: [SCALE_LIN],
    formatter: FORMATTER_PERCENT,

    tier: TIER_1,
  },
  {
    name: 'Realized Price',
    shortName: 'Realized Price',
    description: { default: 'Realized Price is the Realized Cap divided by the current supply.' },
    path: 'market/price_realized_usd',
    categories: [CATEGORIES.priceAndMarketCap],
    group: 'Price',

    defaultScale: SCALE_LOG,
    logTickInterval: 1,
    formatter: FORMATTER_CURRENCY_USD,
    sameAxis: true,

    tier: TIER_2,
  },
  {
    name: 'Market Cap',
    shortName: 'Market Cap',
    description: {
      default:
        'The market capitalization (or network value) is defined as the product of the current supply by the current USD price.',
    },
    path: 'market/marketcap_usd',
    categories: [CATEGORIES.priceAndMarketCap],
    group: 'Market Cap',

    formatter: FORMATTER_CURRENCY_USD,
  },
  {
    name: 'Realized Cap',
    shortName: 'Realized Cap',
    description: {
      default:
        'Realized Cap values different part of the supplies at different prices (instead of using the current daily close). Specifically, it is computed by valuing each UTXO by the price when it was last moved.',
    },
    path: 'market/marketcap_realized_usd',
    categories: [CATEGORIES.priceAndMarketCap],
    group: 'Market Cap',

    formatter: FORMATTER_CURRENCY_USD,

    tier: TIER_2,
  },
  {
    name: 'Market Value to Realized Value Ratio (MVRV)',
    shortName: 'MVRV Ratio',
    description: {
      default:
        'Market Value to Realized Value (MVRV) is the ratio between market cap and realised cap. It gives an indication of when the traded price is below a "fair value". This metric was created by [David Puell](https://twitter.com/kenoshaking) and [Murad Muhmudov](https://twitter.com/MustStopMurad). For more information see [this post](https://medium.com/adaptivecapital/bitcoin-market-value-to-realized-value-mvrv-ratio-3ebc914dbaee)',
    },
    path: 'market/mvrv',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.priceAndMarketCap, CATEGORIES.ratios],
    group: 'MVRV',

    tier: TIER_2,
  },
  {
    name: 'MVRV Z-Score',
    shortName: 'MVRV Z-Score',
    description: {
      default: `The MVRV Z-Score is used to assess when ${ASSET_PLACEHOLDER} is over/undervalued relative to its "fair value". When market value is significantly higher than realized value, it has historically indicated a market top (red zone), while the opposite has indicated market bottoms (green zone). Technically, MVRV Z-Score is defined as the ratio between the difference of market cap and realized cap, and the standard deviation of market cap, i.e. (market cap – realized cap) / std(market cap).`,
    },
    path: 'market/mvrv_z_score',
    categories: [CATEGORIES.marketIndicators, CATEGORIES.priceAndMarketCap, CATEGORIES.ratios],
    group: 'MVRV',

    scales: [SCALE_LIN],

    tier: TIER_2,
  },
  {
    name: 'Long Term Holder MVRV',
    shortName: 'LTH-MVRV',
    description: {
      default:
        'Long Term Holder MVRV (LTH-MVRV) is MVRV that takes into account only UTXOs with a lifespan of at least 155 days and serves as an indicator to assess the behaviour of long term investors. For more information see [this article](https://insights.glassnode.com/sth-lth-sopr-mvrv).',
    },
    path: 'market/mvrv_more_155',
    categories: [
      CATEGORIES.marketIndicators,
      CATEGORIES.priceAndMarketCap,
      CATEGORIES.ratios,
      CATEGORIES.longShortTermHolders,
    ],
    group: 'MVRV',

    scales: [SCALE_LOG],

    tier: TIER_3,
  },
  {
    name: 'Short Term Holder MVRV',
    shortName: 'STH-MVRV',
    description: {
      default:
        'Short Term Holder MVRV (STH-MVRV) is MVRV that takes into account only UTXOs younger than 155 days and serves as an indicator to assess the behaviour of short term investors. For more information see [this article](https://insights.glassnode.com/sth-lth-sopr-mvrv).',
    },
    path: 'market/mvrv_less_155',
    categories: [
      CATEGORIES.marketIndicators,
      CATEGORIES.priceAndMarketCap,
      CATEGORIES.ratios,
      CATEGORIES.longShortTermHolders,
    ],
    group: 'MVRV',

    tier: TIER_3,
  },
]);

export const distributionMetrics = enhanceMetrics([
  {
    name: 'Percent of Supply Held by Top 1% Addresses',
    shortName: 'Supply of Top 1% Addresses',
    description: {
      default:
        'The percentage of supply held by the top 1% addresses. Exchange addresses, smart contract addresses, and other special asset-specific addresses (e.g. team fund addresses) are excluded.',
    },
    path: 'distribution/balance_1pct_holders',
    categories: [CATEGORIES.distribution, CATEGORIES.addresses],
    group: 'Wealth Distribution',

    formatter: FORMATTER_PERCENT,
    resolutionOption: false,

    pulse: {
      maxDecimals: 4,
    },

    tier: TIER_2,
  },
  {
    name: 'Gini Coefficient',
    shortName: 'Gini Coefficient',
    description: {
      default:
        'The gini coefficient for the distribution of coins over addresses. Exchange addresses, smart contract addresses, and other special asset-specific addresses (e.g. team fund addresses) are excluded for the computation of the gini.',
    },
    path: 'distribution/gini',
    categories: [CATEGORIES.distribution, CATEGORIES.addresses],
    group: 'Wealth Distribution',

    resolutionOption: false,
    scales: [SCALE_LIN],

    pulse: {
      maxDecimals: 4,
    },

    tier: TIER_2,
  },
  {
    name: 'Herfindahl Index',
    shortName: 'Herfindahl Index',
    description: {
      default:
        "Originally used as a [measure of competition] (https://en.wikipedia.org/wiki/Herfindahl–Hirschman_Index), we adapt the Herfindahl Index as a metric for decentralization. It measures the addresses' shares of the current supply, and is defined as the sum of weighted address balances in the network. A large score indicates high concentration of supply, whereas a small score is an indicator for more evenly distributed funds across addresses. Exchange addresses, smart contract addresses and other special asset-specific addresses (e.g. team fund addresses) are excluded.",
    },
    path: 'distribution/herfindahl',
    categories: [CATEGORIES.distribution, CATEGORIES.addresses],
    group: 'Wealth Distribution',

    resolutionOption: false,

    pulse: {
      maxDecimals: 4,
    },

    tier: TIER_2,
  },
  {
    name: 'Percent of Supply in Smart Contracts',
    shortName: 'Supply in Smart Contracts',
    description: { default: 'The percent of total supply of the token that is held in smart contracts.' },
    path: 'distribution/supply_contracts',
    categories: [CATEGORIES.distribution, CATEGORIES.addresses, CATEGORIES.supply],
    group: 'Smart Contracts',

    formatter: FORMATTER_PERCENT,
    resolutionOption: false,

    pulse: {
      maxDecimals: 4,
    },

    tier: TIER_2,
  },
]);

export const entityMetrics = enhanceMetrics([
  {
    name: 'Number of New Entities',
    shortName: 'New Entities',
    description: {
      default:
        "The number of unique entities that appeared for the first time in a transaction of the native coin in the network. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).",
    },
    path: 'entities/new_count',
    categories: [CATEGORIES.entities, CATEGORIES.networkStats],
    group: 'Entity Growth',

    tier: TIER_3,
  },
  {
    name: 'Entities Net Growth',
    shortName: 'Entities Net Growth',
    description: {
      default: `The net growth of unique entities in the network. This metric is defined as the difference between new entities and "disappearing" entities (entities with a zero balance that had a non–zero balance at the previous timestamp). Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).`,
    },
    path: 'entities/net_growth_count',
    categories: [CATEGORIES.entities, CATEGORIES.networkStats],
    group: 'Entity Growth',

    scales: [SCALE_LIN],

    tier: TIER_3,
  },
  {
    name: 'Number of Entities With Balance ≥ 1k',
    shortName: 'Number of Whales',
    description: {
      default:
        "The number of unique entities holding at least 1k coins.Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).",
    },
    path: 'entities/min_1k_count',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    tier: TIER_3,
  },
  {
    name: 'Number of Active Entities',
    shortName: 'Active Entities',
    description: {
      default:
        "The number of unique entities that were active either as a sender or receiver. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).",
    },
    path: 'entities/active_count',
    categories: [CATEGORIES.entities, CATEGORIES.networkStats],
    group: 'Entity Activity',

    tier: TIER_3,
  },
  {
    name: 'Number of Sending Entities',
    shortName: 'Sending Entities',
    description: {
      default:
        "The number of unique entities that were active as a sender. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).",
    },
    path: 'entities/sending_count',
    categories: [CATEGORIES.entities, CATEGORIES.networkStats],
    group: 'Entity Activity',

    tier: TIER_3,
  },
  {
    name: 'Number of Receiving Entities',
    shortName: 'Receiving Entities',
    description: {
      default:
        "The number of unique entities that were active as a receiver. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).",
    },
    path: 'entities/receiving_count',
    categories: [CATEGORIES.entities, CATEGORIES.networkStats],
    group: 'Entity Activity',

    tier: TIER_3,
  },
  {
    name: 'Percent of Entities in Profit',
    shortName: 'Percent Entities in Profit',
    description: {
      default: `The percentage of entities in the network that are currently in profit, e.g. the entities whose funds where on average bought at prices lower than the current price. "Buy price" is here defined as the price at the time coins were transferred into addresses controlled by the entity. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information this [article](https://medium.com/glassnode-insights/how-many-entities-hold-bitcoin-e945ecc5d0a1).`,
    },
    path: 'entities/profit_relative',
    categories: [CATEGORIES.entities, CATEGORIES.profitLoss],
    group: 'Entities in Profit/Loss',

    formatter: FORMATTER_PERCENT,

    start: {
      BTC: [1280222701, 1280222701],
    },

    tier: TIER_3,
  },
  {
    name: 'Entities Supply Distribution',
    shortName: 'Entities Supply Distribution',
    description: {
      default: `Relative distribution of the circulating supply held by entities with specific balance bands.`,
    },
    path: 'entities/supply_distribution_relative',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,
    formatter: FORMATTER_PERCENT,
    scales: [SCALE_LIN],
    compare: false,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance < 0.001',
    shortName: 'Supply Held by Entities with Balance < 0.001',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances lower than 0.001 coins.`,
    },
    path: 'entities/supply_balance_less_0001',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 0.001 - 0.01',
    shortName: 'Supply Held by Entities with Balance 0.001 - 0.01',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 0.001 and 0.01 coins.`,
    },
    path: 'entities/supply_balance_0001_001',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 0.01 - 0.1',
    shortName: 'Supply Held by Entities with Balance 0.01 - 0.1',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 0.01 and 0.1 coins.`,
    },
    path: 'entities/supply_balance_001_01',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 0.1 - 1',
    shortName: 'Supply Held by Entities with Balance 0.1 - 1',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 0.1 and 1 coins.`,
    },
    path: 'entities/supply_balance_01_1',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 1 - 10',
    shortName: 'Supply Held by Entities with Balance 1 - 10',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 1 and 10 coins.`,
    },
    path: 'entities/supply_balance_1_10',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 10 - 100',
    shortName: 'Supply Held by Entities with Balance 10 - 100',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 10 and 100 coins.`,
    },
    path: 'entities/supply_balance_10_100',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 100 - 1k',
    shortName: 'Supply Held by Entities with Balance 100 - 1k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 100 and 1,000 coins.`,
    },
    path: 'entities/supply_balance_100_1k',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 1k - 10k',
    shortName: 'Supply Held by Entities with Balance 1k - 10k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 1,000 and 10,000 coins.`,
    },
    path: 'entities/supply_balance_1k_10k',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance 10k - 100k',
    shortName: 'Supply Held by Entities with Balance 10k - 100k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances between 10,000 and 100,000 coins.`,
    },
    path: 'entities/supply_balance_10k_100k',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
  {
    name: 'Supply Held by Entities with Balance > 100k',
    shortName: 'Supply Held by Entities with Balance > 100k',
    description: {
      default: `The total circulating supply${CURRENCY_PLACEHOLDER} held by entities with a balances of at least 100,000 coins.`,
    },
    path: 'entities/supply_balance_more_100k',
    categories: [CATEGORIES.entities, CATEGORIES.distribution],
    group: 'Entity Balances',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_3,
  },
]);

if (WITH_INTERNAL_METRICS) {
  entityMetrics.push(
    ...enhanceMetrics([
      {
        name: '🙈Number of Entities in Profit',
        shortName: '🙈Entities in Profit',
        description: {
          default: `The number of entities in the network that are currently in profit, e.g. the entities whose funds where on average bought at prices lower than the current price. "Buy price" is here defined as the price at the time coins were transferred into addresses controlled by the entity. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information this [article](https://medium.com/glassnode-insights/how-many-entities-hold-bitcoin-e945ecc5d0a1).`,
        },
        path: 'entities/profit_count',
        categories: [CATEGORIES.entities, CATEGORIES.profitLoss, CATEGORIES.internal],
        group: 'Entities in Profit/Loss',

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Number of Entities in Loss',
        shortName: '🙈Entities in Loss',
        description: {
          default: `The number of entities in the network that are currently in loss, e.g. the entities whose funds where on average bought at prices higher than the current price. "Buy price" is here defined as the price at the time coins were transferred into addresses controlled by the entity. Entities are defined as a cluster of addresses that are controlled by the same network entity and are estimated through advanced heuristics and Glassnode's proprietary clustering algorithms. Note that entity–based metrics are based on data science techniques and statistical information that changes over time and are therefore mutable – the data is stable, but most recent data points are subject to slight fluctuations as time progresses. For more information this [article](https://medium.com/glassnode-insights/how-many-entities-hold-bitcoin-e945ecc5d0a1).`,
        },
        path: 'entities/loss_count',
        categories: [CATEGORIES.entities, CATEGORIES.profitLoss, CATEGORIES.internal],
        group: 'Entities in Profit/Loss',

        tier: TIER_3,
        isNew: true,
      },
    ]),
  );
}

export const supplyMetrics = enhanceMetrics([
  {
    name: 'Circulating Supply',
    shortName: 'Circulating Supply',
    description: {
      default: `The total amount of all coins${CURRENCY_PLACEHOLDER} ever created/issued, i.e. the circulating supply.`,
    },
    path: 'supply/current',
    categories: [CATEGORIES.supply, CATEGORIES.networkStats],
    group: 'Coin Issuance',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    omitInitialDays: 0,
  },
  {
    name: 'Percent Supply in Profit',
    shortName: 'Percent Supply in Profit',
    description: {
      default:
        'The percentage of circulating supply in profit, i.e. the percentage of existing coins whose price at the time they last moved was lower than the current price. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).',
    },
    path: 'supply/profit_relative',
    categories: [CATEGORIES.supply, CATEGORIES.profitLoss],
    group: 'Supply in Profit/Loss',

    scales: [SCALE_LIN],
    formatter: FORMATTER_PERCENT,

    tier: TIER_2,
  },
  {
    name: 'Total Supply in Profit',
    shortName: 'Supply in Profit',
    description: {
      default: `The circulating supply${CURRENCY_PLACEHOLDER} in profit, i.e. the amount of coins whose price at the time they last moved was lower than the current price. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).`,
    },
    path: 'supply/profit_sum',
    categories: [CATEGORIES.supply, CATEGORIES.profitLoss],
    group: 'Supply in Profit/Loss',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply in Loss',
    shortName: 'Supply in Loss',
    description: {
      default: `The circulating supply${CURRENCY_PLACEHOLDER} in loss, i.e. the amount of coins whose price at the time they last moved was higher than the current price. For more information see [this article](https://medium.com/glassnode-insights/dissecting-bitcoins-unrealised-on-chain-profit-loss-73e735020c8d).`,
    },
    path: 'supply/loss_sum',
    categories: [CATEGORIES.supply, CATEGORIES.profitLoss],
    group: 'Supply in Profit/Loss',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'HODL Waves',
    shortName: 'HODL Waves',
    description: {
      default:
        'Bundle of all active supply age bands, aka HODL waves. Each colored band shows the percentage of %ASSET% in existence that was last moved within the time period denoted in the legend. The concept of HODL waves was first described in [this post](https://www.unchained-capital.com/blog/hodl-waves-1/) by [Unchained Capital](https://twitter.com/unchainedcap).',
    },
    path: 'supply/hodl_waves',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,
    formatter: FORMATTER_PERCENT,
    scales: [SCALE_LIN],

    compare: false,

    tier: TIER_2,
  },
  {
    name: 'Realized Cap HODL Waves ',
    shortName: 'Realized Cap HODL Waves ',
    description: {
      default:
        '[HODL Waves](https://studio.glassnode.com/metrics?a=BTC&m=supply.HodlWaves) weighted by [Realized Price](https://studio.glassnode.com/metrics?a=BTC&m=market.PriceRealizedUsd). The idea for this visualization of HODL waves was first introduced by [@typerbole](https://twitter.com/typerbole).',
    },
    path: 'supply/rcap_hodl_waves',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    movingAverageOption: false,
    movingMedianOption: false,
    resolutionOption: false,
    formatter: FORMATTER_PERCENT_PRECISE,
    scales: [SCALE_LIN],

    compare: false,

    tier: TIER_2,
  },
  {
    name: 'Percent of Supply Last Active 1+ Years Ago',
    shortName: 'Supply Last Active 1+ Years Ago',
    description: { default: 'The percent of circulating supply that has not moved in at least 1 year.' },
    path: 'supply/active_more_1y_percent',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    formatter: FORMATTER_PERCENT,

    tier: TIER_1,
  },
  {
    name: 'Percent of Supply Last Active 2+ Years Ago',
    shortName: 'Supply Last Active 2+ Years Ago',
    description: { default: 'The percent of circulating supply that has not moved in at least 2 years.' },
    path: 'supply/active_more_2y_percent',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    formatter: FORMATTER_PERCENT,

    tier: TIER_2,
  },
  {
    name: 'Percent of Supply Last Active 3+ Years Ago',
    shortName: 'Supply Last Active 3+ Years Ago',
    description: { default: 'The percent of circulating supply that has not moved in at least 3 years.' },
    path: 'supply/active_more_3y_percent',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    formatter: FORMATTER_PERCENT,

    tier: TIER_2,
  },
  {
    name: 'Percent of Supply Last Active 5+ Years Ago',
    shortName: 'Supply Last Active 5+ Years Ago',
    description: { default: 'The percent of circulating supply that has not moved in at least 5 years.' },
    path: 'supply/active_more_5y_percent',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    formatter: FORMATTER_PERCENT,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active < 24h',
    shortName: 'Supply Last Active < 24h',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved in the last 24 hours.`,
    },
    path: 'supply/active_24h',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 1d-1w',
    shortName: 'Supply Last Active 1d-1w',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 1 day and 1 week ago.`,
    },
    path: 'supply/active_1d_1w',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 1w-1m',
    shortName: 'Supply Last Active 1w-1m',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 1 week and 1 month ago.`,
    },
    path: 'supply/active_1w_1m',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 1m-3m',
    shortName: 'Supply Last Active 1m-3m',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 1 month and 3 months ago.`,
    },
    path: 'supply/active_1m_3m',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 3m-6m',
    shortName: 'Supply Last Active 3m-6m',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 3 months and 6 months ago.`,
    },
    path: 'supply/active_3m_6m',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 6m-12m',
    shortName: 'Supply Last Active 6m-12m',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 6 months and 12 months ago.`,
    },
    path: 'supply/active_6m_12m',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 1y-2y',
    shortName: 'Supply Last Active 1y-2y',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 1 year and 2 years ago.`,
    },
    path: 'supply/active_1y_2y',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 2y-3y',
    shortName: 'Supply Last Active 2y-3y',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 2 years and 3 years ago.`,
    },
    path: 'supply/active_2y_3y',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 3y-5y',
    shortName: 'Supply Last Active 3y-5y',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 3 years and 5 years ago.`,
    },
    path: 'supply/active_3y_5y',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 5y-7y',
    shortName: 'Supply Last Active 5y-7y',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 5 years and 7 years ago.`,
    },
    path: 'supply/active_5y_7y',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 7y-10y',
    shortName: 'Supply Last Active 7y-10y',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved between 7 years and 10 years ago.`,
    },
    path: 'supply/active_7y_10y',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Total Supply Last Active 10y',
    shortName: 'Supply Last Active >10y',
    description: {
      default: `The amount of circulating supply${CURRENCY_PLACEHOLDER} last moved more than 10 years ago.`,
    },
    path: 'supply/active_more_10y',
    categories: [CATEGORIES.supply, CATEGORIES.lifespan],
    group: 'Supply Last Active',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Issuance',
    shortName: 'Issuance',
    description: {
      default: `The total amount of new coins${CURRENCY_PLACEHOLDER} added to the current supply, i.e. minted coins or new coins released to the network.`,
    },
    path: 'supply/issued',
    categories: [CATEGORIES.supply, CATEGORIES.miners],
    group: 'Coin Issuance',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    formatter: FORMATTER_CURRENCY,

    tier: TIER_2,
  },
  {
    name: 'Inflation Rate',
    shortName: 'Inflation Rate',
    description: { default: 'The percentage of new coins issued, divided by the current supply.' },
    path: 'supply/inflation_rate',
    categories: [CATEGORIES.supply, CATEGORIES.miners],
    group: 'Coin Issuance',

    defaultScale: SCALE_LOG,
    logTickInterval: 1,
    formatter: FORMATTER_PERCENT,

    tier: TIER_2,
  },
]);

if (WITH_INTERNAL_METRICS) {
  supplyMetrics.push(
    ...enhanceMetrics([
      {
        name: '🙈Long and Short Term Holders Supply in Profit/Loss',
        shortName: '🙈Relative Long/Short Term Holder Supply',
        description: {
          default: `The relative amount of circulating supply of held by long and short term holders in profit/loss.`,
        },
        path: 'supply/lth_sth_profit_loss_relative',
        categories: [CATEGORIES.supply, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply in Profit/Loss',

        formatter: FORMATTER_PERCENT,
        movingAverageOption: false,
        movingMedianOption: false,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Total Supply Held by Long-Term Holders',
        shortName: '🙈Long-Term Holder Supply',
        description: {
          default: `The total amount of circulating supply${CURRENCY_PLACEHOLDER} held by long term holders.`,
        },
        path: 'supply/lth_sum',
        categories: [CATEGORIES.supply, CATEGORIES.distribution, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply',

        requiredParams: [params.ASSET, params.CURRENCY],
        currencyOption: true,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Long-Term Holder Net Position Change',
        shortName: '🙈Long-Term Holder Position Change',
        description: {
          default: `The monthly net position change of long term holders, i.e. the 30 day change in supply held by long term holders.`,
        },
        path: 'supply/lth_net_change',
        categories: [CATEGORIES.supply, CATEGORIES.distribution, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply',

        requiredParams: [params.ASSET, params.CURRENCY],
        currencyOption: true,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Total Supply in Profit Held by Long-Term Holders',
        shortName: '🙈Long-Term Holder Supply in Profit',
        description: {
          default: `The total amount of circulating supply${CURRENCY_PLACEHOLDER} that is currently in profit and held by long term holders.`,
        },
        path: 'supply/lth_profit_sum',
        categories: [CATEGORIES.supply, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply in Profit/Loss',

        requiredParams: [params.ASSET, params.CURRENCY],
        currencyOption: true,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Total Supply in Loss Held by Long-Term Holders',
        shortName: '🙈Long-Term Holder Supply in Loss',
        description: {
          default: `The total amount of circulating supply${CURRENCY_PLACEHOLDER} that is currently at loss and held by long term holders.`,
        },
        path: 'supply/lth_loss_sum',
        categories: [CATEGORIES.supply, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply in Profit/Loss',

        requiredParams: [params.ASSET, params.CURRENCY],
        currencyOption: true,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Total Supply Held by Short-Term Holders',
        shortName: '🙈Short-Term Holder Supply',
        description: {
          default: `The total amount of circulating supply${CURRENCY_PLACEHOLDER} held by short term holders.`,
        },
        path: 'supply/sth_sum',
        categories: [CATEGORIES.supply, CATEGORIES.distribution, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply',

        requiredParams: [params.ASSET, params.CURRENCY],
        currencyOption: true,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Total Supply in Profit Held by Short-Term Holders',
        shortName: '🙈Short-Term Holder Supply in Profit',
        description: {
          default: `The total amount of circulating supply${CURRENCY_PLACEHOLDER} that is currently in profit and held by short term holders.`,
        },
        path: 'supply/sth_profit_sum',
        categories: [CATEGORIES.supply, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply in Profit/Loss',

        requiredParams: [params.ASSET, params.CURRENCY],
        currencyOption: true,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Total Supply in Loss Held by Short-Term Holders',
        shortName: '🙈Short-Term Holder Supply in Loss',
        description: {
          default: `The total amount of circulating supply${CURRENCY_PLACEHOLDER} that is currently at loss and held by short term holders.`,
        },
        path: 'supply/sth_loss_sum',
        categories: [CATEGORIES.supply, CATEGORIES.profitLoss, CATEGORIES.longShortTermHolders, CATEGORIES.internal],
        group: 'LTH/STH Supply in Profit/Loss',

        requiredParams: [params.ASSET, params.CURRENCY],
        currencyOption: true,

        tier: TIER_3,
        isNew: true,
      },
      {
        name: '🙈Adjusted Circulating Supply',
        shortName: '🙈Adjusted Supply',
        description: {
          default: `The circulating supply adjusted by accounting for lost coins. The amount of lost coins is estimated by all those that have not moved in over 7 years.`,
        },
        path: 'supply/current_adjusted',
        categories: [CATEGORIES.supply, CATEGORIES.networkStats, CATEGORIES.internal],
        group: 'Coin Issuance',

        formatter: FORMATTER_CURRENCY_USD,

        tier: TIER_2,
        isNew: true,
      },
    ]),
  );
}

export const futuresMetrics = enhanceMetrics([
  {
    name: 'Futures Volume (Latest 24h)',
    shortName: 'Futures Volume (Latest 24h)',
    description: {
      default: `The total volume${CURRENCY_PLACEHOLDER} traded in futures contracts per exchange over the last 24 hours. Values are updated every 10 min.`,
    },
    path: 'derivatives/futures_volume_daily_latest',
    categories: [CATEGORIES.derivatives, CATEGORIES.volume, CATEGORIES.exchanges],
    group: 'Futures Volume',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    defaultCurrency: CURRENCY_USD,
    datePickerOption: false,
    formatter: FORMATTER_CURRENCY,
    movingAverageOption: false,
    movingMedianOption: false,
    upgradeOption: false,
    chartType: CHART_TYPE_COLUMN,

    omitInitialDays: 0,
    compare: false,
    price: false,
  },
  {
    name: 'Futures Volume',
    shortName: 'Futures Volume',
    description: {
      default: `The total volume${CURRENCY_PLACEHOLDER} traded in futures contracts in the last 24 hours. Supported exchanges for this metric: Binance, Bitfinex, BitMEX, CoinFLEX, Deribit, FTX, Huobi, and Kraken. Use dropdown above chart to select individual exchanges.`,
    },
    path: 'derivatives/futures_volume_daily_all_sum',
    categories: [CATEGORIES.derivatives, CATEGORIES.volume, CATEGORIES.exchanges],
    group: 'Futures Volume',

    requiredParams: [params.ASSET, params.EXCHANGE, params.CURRENCY],
    currencyOption: true,
    defaultCurrency: CURRENCY_USD,
    exchangeOption: true,
    formatter: FORMATTER_CURRENCY,
    defaultPriceScale: SCALE_LIN,

    omitInitialDays: 0,
  },
  {
    name: 'Futures Volume Perpetual',
    shortName: 'Futures Volume Perpetual',
    description: {
      default: `The total volume${CURRENCY_PLACEHOLDER} traded in perpetual (non-expiring) futures contracts in the last 24 hours. Supported exchanges for this metric: Binance, Bitfinex, BitMEX, CoinFLEX, Deribit, FTX, and Kraken. Use dropdown above chart to select individual exchanges.`,
    },
    path: 'derivatives/futures_volume_daily_perpetual_sum',
    categories: [CATEGORIES.derivatives, CATEGORIES.volume, CATEGORIES.exchanges],
    group: 'Futures Volume',

    requiredParams: [params.ASSET, params.EXCHANGE, params.CURRENCY],
    currencyOption: true,
    defaultCurrency: CURRENCY_USD,
    exchangeOption: true,
    formatter: FORMATTER_CURRENCY,
    defaultPriceScale: SCALE_LIN,

    omitInitialDays: 0,
  },
  {
    name: 'Futures Open Interest (Current)',
    shortName: 'Futures Open Interest (Current)',
    description: {
      default:
        'The current amount of allocated funds in futures contracts per exchange. Values are updated every 10 min.',
    },
    path: 'derivatives/futures_open_interest_latest',
    categories: [CATEGORIES.derivatives, CATEGORIES.exchanges],
    group: 'Futures Open Interest',

    requiredParams: [params.ASSET, params.CURRENCY],
    currencyOption: true,
    defaultCurrency: CURRENCY_USD,
    formatter: FORMATTER_CURRENCY,
    datePickerOption: false,
    movingAverageOption: false,
    movingMedianOption: false,
    upgradeOption: false,
    chartType: CHART_TYPE_COLUMN,

    omitInitialDays: 0,
    compare: false,
    price: false,
  },
  {
    name: 'Futures Open Interest',
    shortName: 'Futures Open Interest',
    description: {
      default: `The total amount of funds${CURRENCY_PLACEHOLDER} allocated in open futures contracts. Supported exchanges for this metric: Binance, Bitfinex, BitMEX, Deribit, FTX, and Huobi. Use dropdown above chart to select individual exchanges.`,
    },
    path: 'derivatives/futures_open_interest_all_sum',
    categories: [CATEGORIES.derivatives, CATEGORIES.exchanges],
    group: 'Futures Open Interest',

    requiredParams: [params.ASSET, params.EXCHANGE, params.CURRENCY],
    currencyOption: true,
    defaultCurrency: CURRENCY_USD,
    exchangeOption: true,
    formatter: FORMATTER_CURRENCY,
    defaultPriceScale: SCALE_LIN,

    omitInitialDays: 0,
  },
  {
    name: 'Futures Open Interest Perpetual',
    shortName: 'Futures Open Interest Perpetual',
    description: {
      default: `The total amount of funds${CURRENCY_PLACEHOLDER} allocated in open perpetual (non-expiring) futures contracts. Supported exchanges for this metric: Binance, Bitfinex, BitMEX, Deribit, and FTX. Use dropdown above chart to select individual exchanges.`,
    },
    path: 'derivatives/futures_open_interest_perpetual_sum',
    categories: [CATEGORIES.derivatives, CATEGORIES.exchanges],
    group: 'Futures Open Interest',

    requiredParams: [params.ASSET, params.EXCHANGE, params.CURRENCY],
    currencyOption: true,
    defaultCurrency: CURRENCY_USD,
    exchangeOption: true,
    formatter: FORMATTER_CURRENCY,
    defaultPriceScale: SCALE_LIN,

    omitInitialDays: 0,
  },
  {
    name: 'Futures Perpetual Funding Rate',
    shortName: 'Futures Perpetual Funding Rate',
    description: {
      default:
        'The average funding rate (in %) set by exchanges for perpetual futures contracts. When the rate is positive, long positions periodically pay short positions. Conversely, when the rate is negative, short positions periodically pay long positions. Supported exchanges for this metric: Binance, Bitfinex, BitMEX, and Deribit. Use dropdown above chart to select individual exchanges.',
    },
    path: 'derivatives/futures_funding_rate_perpetual',
    categories: [CATEGORIES.derivatives, CATEGORIES.exchanges],
    group: 'Futures Funding Rates',

    requiredParams: [params.ASSET, params.EXCHANGE],
    exchangeOption: true,
    formatter: FORMATTER_PERCENT_PRECISE,
    scales: [SCALE_LIN],
    defaultPriceScale: SCALE_LIN,

    omitInitialDays: 0,
  },
]);

export const internalMetrics = [];

if (WITH_INTERNAL_METRICS) {
  internalMetrics.push(
    ...enhanceMetrics([
      {
        name: '🦄Number of Entities with Balance ≥ 10k',
        shortName: '🦄Entities with Balance ≥ 10k',
        description: {
          default:
            "The number of unique entities holding at least 1k coins. Entities are defined as a cluster of addresses that are controlled by one entity and are estimated through Glassnode's internal flags database as well as proprietary clustering algorithms. Note that entity–based metrics are based on clustering and data science techniques and therefore mutable – the data is stable, but recent datapoints are subject to minor fluctuations.",
        },
        path: 'entities/min_10k_count',
        categories: [CATEGORIES.internal],
        group: 'Internal',

        tier: TIER_3,
      },
      {
        name: '🦄Number of Entities with Balance ≥ 100k',
        shortName: '🦄Entities with Balance ≥ 100k',
        description: {
          default:
            "The number of unique entities holding at least 100k coins. Entities are defined as a cluster of addresses that are controlled by one entity and are estimated through Glassnode's internal flags database as well as proprietary clustering algorithms. Note that entity–based metrics are based on clustering and data science techniques and therefore mutable – the data is stable, but recent datapoints are subject to minor fluctuations.",
        },
        path: 'entities/min_100k_count',
        categories: [CATEGORIES.internal],
        group: 'Internal',

        tier: TIER_3,
      },
      {
        name: '🦄Number of Dying Entities',
        shortName: '🦄Dying Entities',
        description: {
          default:
            "The number of unique entities with a zero balance, that had a non–zero balance at the previous timestamp. Entities are defined as a cluster of addresses that are controlled by one entity and are estimated through Glassnode's internal flags database as well as proprietary clustering algorithms. Note that entity–based metrics are based on clustering and data science techniques and therefore mutable – the data is stable, but recent datapoints are subject to minor fluctuations.",
        },

        path: 'entities/dying_count',
        categories: [CATEGORIES.internal],
        group: 'Internal',

        tier: TIER_3,
      },
    ]),
  );
}

export const CATALOG_METRICS = [
  ...accountBasedMetrics,
  ...addressMetrics,
  ...blocksMetrics,
  ...distributionMetrics,
  ...entityMetrics,
  ...exchangeMetrics,
  ...feesMetrics,
  ...futuresMetrics,
  ...indexesMetrics,
  ...indicatorsMetrics,
  ...internalMetrics,
  ...marketMetrics,
  ...miningMetrics,
  ...supplyMetrics,
  ...transactionMetrics,
  ...utxoMetrics,
];

export const METRICS_OPTIONS = keyBy(
  [
    ...accountBasedMetrics,
    ...addressMetrics,
    ...blocksMetrics,
    ...futuresMetrics,
    ...distributionMetrics,
    ...entityMetrics,
    ...exchangeMetrics,
    ...feesMetrics,
    ...indexesMetrics,
    ...indicatorsMetrics,
    ...internalMetrics,
    ...marketMetrics,
    ...miningMetrics,
    ...priceMetrics,
    ...supplyMetrics,
    ...transactionMetrics,
    ...utxoMetrics,
  ],
  'code',
);

export const METRICS_OPTIONS_PATH_KEYD = keyBy(
  [
    ...accountBasedMetrics,
    ...addressMetrics,
    ...blocksMetrics,
    ...futuresMetrics,
    ...distributionMetrics,
    ...entityMetrics,
    ...exchangeMetrics,
    ...feesMetrics,
    ...indexesMetrics,
    ...indicatorsMetrics,
    ...internalMetrics,
    ...marketMetrics,
    ...miningMetrics,
    ...priceMetrics,
    ...supplyMetrics,
    ...transactionMetrics,
    ...utxoMetrics,
  ],
  'path',
);
